import { Municipality } from "@/common/articleTypes";
import {
  Company,
  CompanyRole,
  CompanyroleId,
  EnterpriseAccountInfo,
  SuggestedCompany,
} from "@/types/CompanyInfo";
import { ShareholderDTO, UpdateShareholderEmailBatchResponse } from "@/types/Shareholder";
import { isBefore, plusYears, toYearISOString } from "@/utils/Dates";
import { compact, isEqual, last, maxBy, orderBy, sortBy, sumBy, uniq, without } from "lodash";
import { getFullName } from "@/utils/Roles";
import { User } from "@/types/UserInfo";
import { formatPhoneNumberToE164 } from "@/utils/phoneNumbers";
import {
  ArticleChangeType,
  BR_DECISION_STATUS,
  CompanyItem,
  CustomizedFreetextType,
  DocumentLanguage,
  failure,
  Failure,
  FreetextType,
  GetProjectDocumentRecipientsResponse,
  GetProjectDocumentSignersResponse,
  isOk,
  LocalizedText,
  LocalizedTextForDocument,
  ok,
  Ok,
  pending,
  PhoneNumber,
  ProductName,
  ProjectDocumentPreview,
  Result,
  Signer,
  SigningMethod,
  SubscriptionTypeModel,
  TranslationOption,
} from "@/common/common";
import {
  Decision,
  DecisionData,
  Document,
  DocumentData,
  DocumentOverrides,
} from "@/dbot/project/projectDomain";
import { isProductionEnvironment } from "@/utils/environments";
import type { OptionTranche } from "@/instruments/optionsDomain";
import {
  DocumentSigningStatus,
  GetDocumentSigningStatusResponse,
  GetSubscriptionStatusResponse,
} from "@/document/documentApi";
import {
  FileUploadConfig,
  filterFileUploadConfig,
  ProjectDecisionAttachment,
  UploadedAttachment,
} from "@/fileUpload/fileUploadDomain";
import { EmailStatus } from "@/types/SavedDocument";
import { BoardMeeting, BoardMeetingFields, ExtraParticipant } from "./commonBoardMeetingDomain";
import { CreateAndSendMessageRequest } from "@/messaging/adminMessagingDomain";
import { AIClient } from "@/ai/AIAssistant";
import {
  ContributionPerShareType,
  IncreaseOfShareCapitalType,
  PeriodForSubscription,
  ShareIssuePerShareType,
  SubscriberForIncreaseNominalValue,
  SubscriberForIssue,
  SubscriberSigner,
} from "@/dbot/increaseOfShareCapital/increaseOfShareCapitalDomain";
import { IconName } from "@/components-v2/iconMapping";
import { FreetextTypeForDocument } from "@/dbot/freetext/freetextDomain";
import { findMergerWithCounterpart, getMergerCompanyPanels } from "@/dbot/merger/mergerDomain";
import { IconProps } from "@/components-v2/Icon";
import {
  enableSendToBRDate,
  OptionsBeforeSending,
  RecipientsEditModel,
} from "@/dbot/summary/summaryDomain";
import { BoardMeetingVote } from "@/dbot/boardMeetingMinutes/boardMeetingVoting";
import { UserPrefs } from "@/stores/userPrefsDomain";
import {
  ElectronicGeneralMeeting,
  GeneralMeetingStatus,
  StatusName,
} from "@/meetings/generalMeeting/electronicGeneralMeetingDomain";
import { Language } from "@/i18n/languages";
import {
  GeneralMeeting,
  GeneralMeetingFields,
  GeneralMeetingVotes,
  GeneralMeetingVoting,
  GeneralMeetingVotingType,
} from "./commonGeneralMeetingDomain";
import {
  GeneralMeetingVoteType,
  PoARecipientRole,
} from "@/meetings/generalMeeting/generalMeetingDomain";
import { when } from "@/utils/functions";
import { formatOrgNum } from "@/utils/other";
import { TMessage, TMessageParam } from "@/i18n/tt";
import { DecisionType, isDecisionType } from "@/common/decisions";
import { DocumentType, getDocumentLabel } from "@/common/documents";
import { ArticlesOfAssociation } from "@/types/ArticlesOfAssociation";
import { FilePreviewProps } from "@/components-v2/FilePreview";
import { FileToPreview } from "@/components-v2/BatchPreviewDialog/batchPreviewDomain";
import { ChangeEvent } from "react";
import { defaultLanguageForOrgNo, isSwedishOrgNo } from "@/utils/orgNo";
import {
  getDecisionsByCategory,
  ProjectTemplateAccess,
} from "@/dbot/projectList/projectListDomain";
import { AlertMessage } from "@/stores/AlertMessageStore";
import { DCEvent, EventDispatcher, navigateTo, thunk } from "@/events/eventsDomain";
import { adminProjects, navigationState } from "@/utils/navigation";
import {
  BooleanFieldModel,
  CompaniesForPreview,
  DocumentsForPreview,
  LocalizedStringFieldModel,
  RadioFieldModel,
  SigningSettings,
} from "@/common/commonForms";
import { PaidSubscription, SubscriptionType } from "@/common/subscription";
import { RootStoreData } from "@/events/rootStoreData";
import { ShareType, ShareTypeCode } from "@/common/share";
import { LocalizedTextEditorModel } from "@/text/localizedTextEditorDomain";
import { TMessageKey } from "@/i18n/types.gen";
import {
  CustomDocumentInitialRecipients,
  CustomDocumentType,
} from "@/dbot/customDocument/customDocumentDomain";
import { EventLog, EventType } from "@/common/eventLogs";
import { getAllCountriesSortedByName, norway } from "@/utils/countries";
import {
  DestinationFolder,
  DestinationFoldersResponse,
  getEntryLabel,
} from "@/archive/archiveDomain";
import { makeAutoObservable } from "mobx";
import { AlertProps } from "@/components-v2/Alert";
import {
  recipientIdFromUrnReference,
  resolveUrnReference,
  UrnReference,
} from "@/utils/UrnReference";
import { next, prev, toggleElement } from "@/utils/arrays";
import Big from "big.js";
import { FeatureToggles } from "@/common/featureToggles";
import { TagProps } from "@/components-v2/Tag";
import { CheckboxGroupProps } from "@/components-v2/Checkbox";
import { orderlessFilter } from "@/utils/filter";
import { UploadedAttachmentProps } from "@/dbot/attachments";
import { formatDate } from "@/i18n/dateFormat";
import { RoleType } from "@/types/enums/RoleType";
import { UpdatedPreview } from "./projectApi";
import { toQueryString } from "@/utils/queryParams";
import { DropdownMenuItemType } from "@/components-v2/Dropdown.tsx";
import { prepToShowUseTemplateDialog } from "@/templates/templateListDomain";
import { TemplateType, templateTypeSegments } from "@/templates/templateDomain";
import {
  TemplateAttachment,
  templateDocumentTitle,
  TemplateItem,
} from "@/templates/saveTemplateDomain";

///
/// Project
///

export enum ProjectStatus {
  DRAFT = "DRAFT",
  DOCUMENT_SIGNING_STARTED = "DOCUMENT_SIGNING_STARTED", //legacy, to be removed
  SENT = "SENT",
  FOLLOW_UP = "FOLLOW_UP",
  COMPLETED = "COMPLETED",
}

export const projectStatusLabels: Record<ProjectStatus, TMessageKey> = {
  [ProjectStatus.DRAFT]: "projectStatus.DRAFT",
  [ProjectStatus.DOCUMENT_SIGNING_STARTED]: "projectStatus.SENT",
  [ProjectStatus.SENT]: "projectStatus.SENT",
  [ProjectStatus.FOLLOW_UP]: "projectStatus.FOLLOW_UP",
  [ProjectStatus.COMPLETED]: "projectStatus.COMPLETED",
};

export interface ProjectJsonData {
  decisions: Decision[];
  documents: Document[];
  boardMeeting?: BoardMeeting;
  generalMeeting?: GeneralMeeting;
}

export type DissolutionStatus = {
  generalMeetingDate?: string;
  brRegisteredDate?: string;
  creditorNoticeExpiryDate?: string;
  isCreditorNoticeExpired: boolean;
};

export type GrantLetterCandidate = {
  grantId: string;
  planId: string;
  holderId: string;
  holderName: string;
  shareholderId?: number;
  programName: string;
  numberOfOptions: number;
  grantDate?: string;
};

export type ExerciseCandidate = {
  planId: string;
  programName: string;
  grantId: string;
  trancheId: string;
  trancheName?: string;
  holderId: string;
  holderName: string;
  shareholderId?: number;
  shareTypeId?: number;
  shareTypeCode?: ShareTypeCode;
  strikePrice: number;
  exercisable: number;
  isCompany?: boolean;
  tranche?: OptionTranche;
  holderEmail?: string;
};

export interface ProjectContextData {
  initialState: Evaluation;
  inputStates: InputState[];
  optionGrants?: GrantLetterCandidate[];
  optionExercises?: ExerciseCandidate[];
}

export function createProjectJsonData(jsonData?: Partial<ProjectJsonData>): ProjectJsonData {
  return {
    ...jsonData,
    decisions: jsonData?.decisions ?? [],
    documents: jsonData?.documents ?? [],
  };
}

export interface ProjectsForCopyResponse {
  projects: ProjectForCopy[];
}

export interface ProjectForCopy {
  projectId: string;
  title: string;
  decisions: DecisionForCopy[];
}

export interface DecisionForCopy {
  decisionId: string;
  decisionType: DecisionType;
  freetextTitles: LocalizedText[];
}

export interface ProjectListItem {
  id: string;
  organizationnumber: string;
  projectName: string;
  projectStatus: ProjectStatus;
  updatedTime: string;
  hasBoardMeeting: boolean;
  hasGeneralMeeting: boolean;
  containsNoticeOrMinutesBoardMeetingDocument: boolean;
  containsNoticeOrMinutesGeneralMeetingDocument: boolean;
  boardMeetingDate?: string;
  boardMeetingTime?: string;
  boardMeetingNumber?: string;
  useBoardMeetingNumber?: boolean;
  generalMeetingDate?: string;
  generalMeetingTime?: string;
  isBatchProject: boolean;
  isOwner: boolean;
  batchProjectCompanyName?: string;
  numberOfPermissions: number;
  isMainCompany?: boolean;
}

export type ProjectDecisionItemDTO = {
  decisionId: string;
  decisionType: DecisionType;
  projectId: string;
  projectName: string;
  orgNo: string;
  companyName: string;
  titles?: LocalizedText[];
  isStarred: boolean;
  updatedAt: string;
  attachments?: ProjectDecisionAttachment[];
};

export type DocumentPreview = {
  documentId: string;
  documentType: DocumentType;
  orgNo: string;
  content?: string;
  url?: string;
  baseVersionContent?: string;
  numberOfSigners?: number;
  numberOfSignatures?: number;
  signingStatus?: DocumentSigningStatus;
  isPreviewInProgress?: boolean;
  attachments?: AttachmentPreview[];
};

export type AttachmentPreview = {
  type: AutoAttachmentType;
  content: string;
};

export type AutoAttachmentType =
  | "gmRepresentation"
  | "subscribersAsAttachment"
  | "subscriptionDocumentAsAttachment"
  | "incorporationAoA";

export const autoAttachmentTypeLabels: { [key in AutoAttachmentType]: TMessageKey } = {
  gmRepresentation: "auto-attachment.gmRepresentation",
  subscribersAsAttachment: "auto-attachment.subscribersAsAttachment",
  subscriptionDocumentAsAttachment: "auto-attachment.subscriptionDocumentAsAttachment",
  incorporationAoA: "archive.articles",
};

export type ProjectCompany = {
  orgNo: string;
  name: string;
  isMainCompany?: boolean;
  isUnderIncorporation?: boolean;
};

export type BatchDocumentOverrrides = {
  documentId: string;
  orgNo: string;
  overrides?: DocumentOverrides;
};

export type ProjectCompanyData = {
  documentOverrides: { [documentId: string]: DocumentOverrides };
};

export type GetProjectResponse = {
  projectId: string;
  version: number;
  name?: string;
  generatedName?: string;
  orgNo: string;
  projectStatus: ProjectStatus;
  jsonData: ProjectJsonData;
  contextData: {
    [orgNo: string]: ProjectContextData;
  };
  previews: DocumentPreview[];
  documentStatuses: ProjectDocumentStatus[];
  isLocked: boolean;
  isDeleted: boolean;
  isReadonly: boolean;
  isSharepointEnabled: boolean;
  followUps: DecisionFollowUp[];
  boardMeetingVotes: BoardMeetingVote[];
  electronicGeneralMeeting?: ElectronicGeneralMeeting;
  participationList: GeneralMeetingParticipation[];
  powerOfAttorneys: PowerOfAttorneyForGeneralMeeting[];
  decisionsRequiresGeneralMeeting: string[];
  decisionsRequiringArticlesOfAssociation: string[];
  defaultDecisionsGeneralMeetingVotingType: Record<string, GeneralMeetingVotingType>;
  decisionsApprovedByGeneralMeeting: Record<string, boolean>;
  numberOfPermissions: number;
  subscribers: Subscriber[];
  aoaMetadata?: AoAMetadata;
  companies: string[];
  companyData: { [orgNo: string]: ProjectCompanyData };
  dissolutionStatus?: DissolutionStatus;
};

export const isAoAUpdateInProgress = (md?: AoAMetadata): boolean =>
  md?.aoaUpdate?.state?.type === "queued" || md?.aoaUpdate?.state?.type === "inprogress";

export type AoAMetadata = {
  isSharecapitalSingleSentence: boolean;
  aoaUpdate: AoAUpdateDescription;
};

export type AoAChangeAuto = {
  type: "auto";
};

export type AoAChangeAI = {
  type: "ai";
};

export type AoAChangeMode = AoAChangeAuto | AoAChangeAI;

export type AoAUpdateDescription = {
  changeMode: AoAChangeMode;
  state: AoAUpdateState;
};

export type Queued = {
  type: "queued";
};

export type InProgress = {
  type: "inprogress";
};

export type Success = {
  type: "success";
};

export type AoAUpdateState = Queued | InProgress | Success;

///
/// Decisions
///

const enabledDecisionTypes: DecisionType[] = compact([
  DecisionType.approveAnnualAccount,
  DecisionType.approveAuditorRemuneration,
  DecisionType.approveCompanyBoardRemuneration,
  DecisionType.approveNominationCommitteeRemuneration,
  DecisionType.auditor,
  DecisionType.boardAssignsSignature,
  DecisionType.capitalIncreaseThroughBonusIssue,
  DecisionType.changeBusinessPurpose,
  DecisionType.changeOfAddress,
  DecisionType.changeOfBusinessManager,
  DecisionType.changeArticlesOfAssociation,
  DecisionType.changeCompanyBoardInArticlesOfAssociation,
  DecisionType.changeSignatureInArticlesOfAssociation,
  DecisionType.companyName,
  DecisionType.convertibleLoanPowerOfAttorney,
  DecisionType.conversionOfShares,
  DecisionType.dividend,
  DecisionType.dividendPowerOfAttorney,
  DecisionType.electionOfCompanyBoard,
  DecisionType.electNominationCommittee,
  DecisionType.employmentOfManagingDirector,
  DecisionType.freetext,
  DecisionType.groupContributionByGeneralMeeting,
  DecisionType.incorporation,
  DecisionType.increaseOfShareCapital,
  DecisionType.increaseShareCapitalPowerOfAttorney,
  DecisionType.insertCompanyBoardInArticlesOfAssociation,
  DecisionType.insertSignatureInArticlesOfAssociation,
  DecisionType.merger,
  DecisionType.interimBalanceSheet,
  DecisionType.dissolveCompany,
  DecisionType.liquidateCompany,
  DecisionType.noAuditor,
  DecisionType.procura,
  DecisionType.reductionOfShareCapital,
  DecisionType.removeCompanyBoardFromArticlesOfAssociation,
  DecisionType.removeSignatureFromArticlesOfAssociation,
  DecisionType.reverseSplitShare,
  DecisionType.splitShare,
  DecisionType.acquireOwnSharesPowerOfAttorney,
  DecisionType.accountant,
  DecisionType.noAccountant,
  DecisionType.noBusinessManager,
  DecisionType.noManagingDirector,
  !isProductionEnvironment() && DecisionType.shareTransferConsent,
  DecisionType.optionGrantLetter,
  DecisionType.optionExercise,
]);

export const enabledBatchDecisionTypes: DecisionType[] = compact([
  DecisionType.approveAnnualAccount,
  DecisionType.approveAuditorRemuneration,
  DecisionType.auditor,
  DecisionType.changeOfAddress,
  DecisionType.electionOfCompanyBoard,
  DecisionType.employmentOfManagingDirector,
  DecisionType.freetext,
  DecisionType.accountant,
  DecisionType.noAuditor,
  DecisionType.noManagingDirector,
]);

export const exclusiveDecisionTypes = new Set<DecisionType>([DecisionType.incorporation]);

export const decisionsRequiringCustomization = new Set([
  DecisionType.approveCompanyBoardRemuneration,
  DecisionType.boardAssignsSignature,
  DecisionType.capitalIncreaseThroughBonusIssue,
  DecisionType.changeBusinessPurpose,
  DecisionType.changeArticlesOfAssociation,
  DecisionType.changeCompanyBoardInArticlesOfAssociation,
  DecisionType.changeSignatureInArticlesOfAssociation,
  DecisionType.companyName,
  DecisionType.convertibleLoanPowerOfAttorney,
  DecisionType.dividend,
  DecisionType.reductionOfShareCapital,
  DecisionType.removeSignatureFromArticlesOfAssociation,
  DecisionType.reverseSplitShare,
  DecisionType.splitShare,
  DecisionType.increaseOfShareCapital,
  DecisionType.approveNominationCommitteeRemuneration,
  DecisionType.conversionOfShares,
  DecisionType.dividendPowerOfAttorney,
  DecisionType.electNominationCommittee,
  DecisionType.groupContributionByGeneralMeeting,
  DecisionType.increaseShareCapitalPowerOfAttorney,
  DecisionType.insertCompanyBoardInArticlesOfAssociation,
  DecisionType.insertSignatureInArticlesOfAssociation,
  DecisionType.procura,
  DecisionType.removeCompanyBoardFromArticlesOfAssociation,
  DecisionType.acquireOwnSharesPowerOfAttorney,
  DecisionType.interimBalanceSheet,
]);

export const decisionsWithOwnAttachments = new Set<DecisionType>([
  DecisionType.interimBalanceSheet,
]);

const newDecisionTypes: DecisionType[] = [
  DecisionType.noManagingDirector,
  DecisionType.shareTransferConsent,
];

const optionDecisionTypes: DecisionType[] = [
  DecisionType.optionGrantLetter,
  DecisionType.optionExercise,
];

export function getEnabledDecisionTypes({
  isBatchProject,
  featureToggles,
}: {
  isBatchProject?: boolean;
  featureToggles?: FeatureToggles;
}): DecisionType[] {
  if (isBatchProject) {
    return enabledBatchDecisionTypes;
  }
  const decisionTypes = isProductionEnvironment() ? enabledDecisionTypes : getAllDecisionTypes();
  return featureToggles?.financialInstrumentOptions
    ? decisionTypes
    : decisionTypes.filter((d) => !optionDecisionTypes.includes(d));
}

export function getAllDecisionTypes(orgNo?: string): DecisionType[] {
  if (isSwedishOrgNo(orgNo)) {
    return [DecisionType.freetext, DecisionType.companyName, DecisionType.electionOfCompanyBoard];
  }
  return Object.values(DecisionType).filter((d) => d !== DecisionType.assignsNewCompanyBoard);
}

export function isDecisionTypeNew(decisionType: DecisionType) {
  return newDecisionTypes.includes(decisionType);
}

export enum DecisionCategory {
  aboutTheCompany = "aboutTheCompany",
  changesToArticles = "changesToArticles",
  companyRoles = "companyRoles",
  companyShareCapital = "companyShareCapital",
  corporateEvents = "corporateEvents",
  ordinaryGeneralMeeting = "ordinaryGeneralMeeting",
  powerOfAttorneyFromGeneralMeeting = "powerOfAttorneyFromGeneralMeeting",
  options = "options",
  other = "other",
}

export const decisionCategoryLabels: { [key in DecisionCategory]: TMessage } = {
  [DecisionCategory.corporateEvents]: { key: "dbot.category.corporateEvents" },
  [DecisionCategory.ordinaryGeneralMeeting]: { key: "dbot.category.ordinaryGeneralMeeting" },
  [DecisionCategory.aboutTheCompany]: { key: "dbot.category.aboutTheCompany" },
  [DecisionCategory.companyRoles]: { key: "dbot.category.companyRoles" },
  [DecisionCategory.companyShareCapital]: { key: "dbot.category.companyShareCapital" },
  [DecisionCategory.changesToArticles]: { key: "dbot.category.changesToArticles" },
  [DecisionCategory.powerOfAttorneyFromGeneralMeeting]: {
    key: "dbot.category.powerOfAttorneyFromGeneralMeeting",
  },
  [DecisionCategory.options]: { key: "dbot.category.options" },
  [DecisionCategory.other]: { key: "dbot.category.other" },
};

const newDecisionCategories: DecisionCategory[] = [
  DecisionCategory.corporateEvents,
  DecisionCategory.options,
];

export function isDecisionCategoryNew(category: DecisionCategory) {
  return newDecisionCategories.includes(category);
}

export const categorySubscriptionMapping: Record<DecisionCategory, SubscriptionType> = {
  [DecisionCategory.corporateEvents]: SubscriptionType.PRO,
  [DecisionCategory.ordinaryGeneralMeeting]: SubscriptionType.BASIC,
  [DecisionCategory.aboutTheCompany]: SubscriptionType.PRO,
  [DecisionCategory.companyRoles]: SubscriptionType.PRO,
  [DecisionCategory.companyShareCapital]: SubscriptionType.PRO,
  [DecisionCategory.changesToArticles]: SubscriptionType.PRO,
  [DecisionCategory.powerOfAttorneyFromGeneralMeeting]: SubscriptionType.PRO,
  [DecisionCategory.options]: SubscriptionType.PRO,
  [DecisionCategory.other]: SubscriptionType.PRO,
};

export const categoryMapping: Record<DecisionType, DecisionCategory> = {
  [DecisionType.changeArticlesOfAssociation]: DecisionCategory.changesToArticles,
  [DecisionType.changeOfAddress]: DecisionCategory.aboutTheCompany,
  [DecisionType.companyName]: DecisionCategory.aboutTheCompany,
  [DecisionType.electionOfCompanyBoard]: DecisionCategory.companyRoles,
  [DecisionType.auditor]: DecisionCategory.companyRoles,
  [DecisionType.noAuditor]: DecisionCategory.companyRoles,
  [DecisionType.approveAnnualAccount]: DecisionCategory.ordinaryGeneralMeeting,
  [DecisionType.incorporation]: DecisionCategory.corporateEvents,
  [DecisionType.merger]: DecisionCategory.corporateEvents,
  [DecisionType.interimBalanceSheet]: DecisionCategory.corporateEvents,
  [DecisionType.dissolveCompany]: DecisionCategory.corporateEvents,
  [DecisionType.liquidateCompany]: DecisionCategory.corporateEvents,
  [DecisionType.dividend]: DecisionCategory.ordinaryGeneralMeeting,
  [DecisionType.approveCompanyBoardRemuneration]: DecisionCategory.ordinaryGeneralMeeting,
  [DecisionType.electNominationCommittee]: DecisionCategory.ordinaryGeneralMeeting,
  [DecisionType.approveNominationCommitteeRemuneration]: DecisionCategory.ordinaryGeneralMeeting,
  [DecisionType.approveAuditorRemuneration]: DecisionCategory.ordinaryGeneralMeeting,
  [DecisionType.groupContributionByGeneralMeeting]: DecisionCategory.ordinaryGeneralMeeting,
  [DecisionType.boardAssignsSignature]: DecisionCategory.companyRoles,
  [DecisionType.changeSignatureInArticlesOfAssociation]: DecisionCategory.changesToArticles,
  [DecisionType.employmentOfManagingDirector]: DecisionCategory.companyRoles,
  [DecisionType.noManagingDirector]: DecisionCategory.companyRoles,
  [DecisionType.increaseOfShareCapital]: DecisionCategory.companyShareCapital,
  [DecisionType.removeCompanyBoardFromArticlesOfAssociation]: DecisionCategory.changesToArticles,
  [DecisionType.insertCompanyBoardInArticlesOfAssociation]: DecisionCategory.changesToArticles,
  [DecisionType.removeSignatureFromArticlesOfAssociation]: DecisionCategory.changesToArticles,
  [DecisionType.insertSignatureInArticlesOfAssociation]: DecisionCategory.changesToArticles,
  [DecisionType.changeCompanyBoardInArticlesOfAssociation]: DecisionCategory.changesToArticles,
  [DecisionType.procura]: DecisionCategory.companyRoles,
  [DecisionType.assignsNewCompanyBoard]: DecisionCategory.companyRoles,
  [DecisionType.capitalIncreaseThroughBonusIssue]: DecisionCategory.companyShareCapital,
  [DecisionType.freetext]: DecisionCategory.other,
  [DecisionType.splitShare]: DecisionCategory.companyShareCapital,
  [DecisionType.reverseSplitShare]: DecisionCategory.companyShareCapital,
  [DecisionType.convertibleLoanPowerOfAttorney]: DecisionCategory.powerOfAttorneyFromGeneralMeeting,
  [DecisionType.dividendPowerOfAttorney]: DecisionCategory.powerOfAttorneyFromGeneralMeeting,
  [DecisionType.increaseShareCapitalPowerOfAttorney]:
    DecisionCategory.powerOfAttorneyFromGeneralMeeting,
  [DecisionType.reductionOfShareCapital]: DecisionCategory.companyShareCapital,
  [DecisionType.conversionOfShares]: DecisionCategory.companyShareCapital,
  [DecisionType.acquireOwnSharesPowerOfAttorney]:
    DecisionCategory.powerOfAttorneyFromGeneralMeeting,
  [DecisionType.accountant]: DecisionCategory.companyRoles,
  [DecisionType.noAccountant]: DecisionCategory.companyRoles,
  [DecisionType.changeOfBusinessManager]: DecisionCategory.companyRoles,
  [DecisionType.noBusinessManager]: DecisionCategory.companyRoles,
  [DecisionType.changeBusinessPurpose]: DecisionCategory.changesToArticles,
  [DecisionType.shareTransferConsent]: DecisionCategory.companyShareCapital,
  [DecisionType.optionGrantLetter]: DecisionCategory.options,
  [DecisionType.optionExercise]: DecisionCategory.options,
};

export type WithDecisionType<T extends DecisionType> = {
  type: T;
};

export type DecisionItem = {
  decisionId: string;
  type: DecisionType;
  title: TMessage;
  decisionTexts: LocalizedTextForDocument[];
  decisionIntroTexts: LocalizedTextForDocument[];
  attachments: ProjectDecisionAttachment[];
  decisionData?: DecisionData;
  includeInGeneralMeeting?: boolean;
  defaultGeneralMeetingVotingType: GeneralMeetingVotingType;
  isApprovedByGeneralMeeting: boolean;
  hasValidationError?: boolean;
};

export type DecisionStatusResponse = {
  followUps: DecisionFollowUp[];
};

export type BRSigner = {
  id?: CompanyroleId;
  name: string;
  role: BRSignerRole;
  email?: string;
};

export enum AdditionalBRSignerReason {
  NEW_TO_BOARD = "NEW_TO_BOARD",
  BOARD_ROLE_CHANGE = "BOARD_ROLE_CHANGE",
  NEW_MANAGING_DIRECTOR = "NEW_MANAGING_DIRECTOR",
  NEW_AUDITOR = "NEW_AUDITOR",
  AUDITOR = "AUDITOR",
  CONFIRMS_SHARE_CAPITAL_CONTRIBUTIONS = "CONFIRMS_SHARE_CAPITAL_CONTRIBUTIONS",
}

export type AdditionalBRSigner = {
  name?: string;
  organisationNumber?: string;
  email?: string;
  reason: AdditionalBRSignerReason;
};

export const brSignerId = (signer: BRSigner): string =>
  signer.id?.toString() ?? `${signer.role}:${signer.name}`;

export function mapToBRSigners(roles: CompanyRole[]) {
  return roles.map((r) => {
    return {
      id: r.companyroleId,
      name: getFullName(r),
      role: mapToBRSignerRole(r.role),
      email: r.email,
    };
  });
}

export function mapToBRSignerRole(role: RoleType): BRSignerRole {
  switch (role) {
    case RoleType.CEO:
      return BRSignerRole.ceo;
    case RoleType.CHAIRPERSON:
      return BRSignerRole.chairperson;
    case RoleType.DEPUTY_CHAIRPERSON:
    case RoleType.MEMBER:
      return BRSignerRole.member;
    default:
      return BRSignerRole.other;
  }
}

export enum BRSignerRole {
  ceo = "ceo",
  chairperson = "chairperson",
  member = "member",
  other = "other",
}

export enum FollowUpStatus {
  READY = "READY",
  IN_PROGRESS = "IN_PROGRESS",
  COMPLETED = "COMPLETED",
}

export const followUpStatusLabels: Record<FollowUpStatus, TMessageKey> = {
  [FollowUpStatus.READY]: "dbot.summary.not-completed",
  [FollowUpStatus.IN_PROGRESS]: "dbot.summary.inProgress",
  [FollowUpStatus.COMPLETED]: "dbot.summary.completed",
};

export enum FollowUpType {
  viewSubscribers = "viewSubscribers",
  subscriptionForm = "subscriptionForm",
  subscriptionFormStatus = "subscriptionFormStatus",
  allocatedShares = "allocatedShares",
  attachment = "attachment",
  identification = "identification",
  sendToBR = "sendToBR",
  apply = "apply",
  signIncorporationDocument = "signIncorporationDocument",
  signBusinessInformation = "signBusinessInformation",
  signMergerPlan = "signMergerPlan",
  sendMergerShareholderNotice = "sendMergerShareholderNotice",
  mergerOwnResolution = "mergerOwnResolution",
  mergerCounterpartResolution = "mergerCounterpartResolution",
  confirmationOfPaidShareCapitalContribution = "confirmationOfPaidShareCapitalContribution",
  confirmationOfAuditorRole = "confirmationOfAuditorRole",
  confirmationOfAccountantRole = "confirmationOfAccountantRole",
  submitToRegisterOfCompanyAccounts = "submitToRegisterOfCompanyAccounts",
  registerInShareholderRegister = "registerInShareholderRegister",
}

export type DecisionFollowUp = {
  decisionId: string;
  orgNo: string;
  uploadedDecisionAttachments: DecisionAttachment[];
  followUps: FollowUp[];
  eventLogs: EventLog[];
  isEnabled: boolean;
};

export type FollowUp = {
  data: FollowUpData;
  status: FollowUpStatus;
  isActive: boolean;
  isForInformation: boolean;
};

export type FollowUpData =
  | ViewSubscribersFollowUp
  | SubscriptionFormFollowUp
  | SubscriptionFormStatusFollowUp
  | AllocatedSharesFollowUp
  | AttachmentFollowUp
  | IdentificationFollowUp
  | SendToBRFollowUp
  | ApplyFollowUp
  | SignIncorporationDocumentFollowUp
  | SignBusinessInformatinoFollowUp
  | SignMergerPlanFollowUp
  | SendMergerShareholderNoticeFollowUp
  | MergerOwnResolutionFollowUp
  | MergerCounterpartResolutionFollowUp
  | ConfirmationOfPaidShareCapitalContributionFollowUp
  | ConfirmationOfAuditorRoleFollowUp
  | ConfirmationOfAccountantRoleFollowUp
  | SubmitToRegisterOfCompanyAccountsFollowUp
  | RegisterInShareholderRegisterFollowUp;

export type ViewSubscribersFollowUp = {
  type: FollowUpType.viewSubscribers;
  numberOfSubscribers: number;
};

export type SignIncorporationDocumentFollowUp = {
  type: FollowUpType.signIncorporationDocument;
};

export type SignBusinessInformatinoFollowUp = {
  type: FollowUpType.signBusinessInformation;
};

export type SignMergerPlanFollowUp = {
  type: FollowUpType.signMergerPlan;
};

export type SendMergerShareholderNoticeFollowUp = {
  type: FollowUpType.sendMergerShareholderNotice;
};

export type MergerOwnResolutionFollowUp = {
  type: FollowUpType.mergerOwnResolution;
};

export type MergerCounterpartResolutionFollowUp = {
  type: FollowUpType.mergerCounterpartResolution;
};

export type ConfirmationOfPaidShareCapitalContributionFollowUp = {
  type: FollowUpType.confirmationOfPaidShareCapitalContribution;
};

export type ConfirmationOfAuditorRoleFollowUp = {
  type: FollowUpType.confirmationOfAuditorRole;
};

export type ConfirmationOfAccountantRoleFollowUp = {
  type: FollowUpType.confirmationOfAccountantRole;
};

export type SubmitToRegisterOfCompanyAccountsFollowUp = {
  type: FollowUpType.submitToRegisterOfCompanyAccounts;
  isSubmitted: boolean;
};

export type RegisterInShareholderRegisterFollowUp = {
  type: FollowUpType.registerInShareholderRegister;
  isRegistered: boolean;
};

export type SubscriptionFormFollowUp = { type: FollowUpType.subscriptionForm; documentId: string };
export type SubscriptionFormStatusFollowUp = {
  type: FollowUpType.subscriptionFormStatus;
  documentId: string;
};
export type AllocatedSharesFollowUp = { type: FollowUpType.allocatedShares };
export type AttachmentFollowUp = {
  type: FollowUpType.attachment;
  attachmentType: AttachmentType;
  attachableDocumentTypes: AttachableDocumentType[];
  requiresEmail: boolean;
  email?: string;
  attachDocumentTypes?: boolean;
  requiresConfirmer: boolean;
  confirmer?: CompanyContact;
};

export type CompanyContact = {
  name?: string;
  organisationNumber?: string;
  businessAddress1?: string;
  businessZip?: string;
  businessCity?: string;
  businessCountry?: string;
};

export type IdentificationFollowUp = {
  type: FollowUpType.identification;
  name?: string;
  ssn?: string;
};

export type SendToBRFollowUp = {
  type: FollowUpType.sendToBR;
  brSigners: BRSigner[];
  additionalSigners: AdditionalBRSigner[];
  brStatus: BR_DECISION_STATUS;
  brPublishingDate?: string;
  updatedDate?: string;
  isRegistration: boolean;
  brResultData?: string;
};

export type DseSubmitAck = {
  messageId: string;
  mottakId: string;
  mottattTidspunkt: string;
  antallVedlegg: number;
};

export type DseStatus = "PENDING" | "RESPONSE_AVAILABLE";

export type DseResponseKind =
  | "UNDER_CASE_PROCESSING"
  | "APPROVED"
  | "NOT_APPROVED"
  | "REJECTED"
  | "OTHER";

export type DseVedtakDocument = {
  navn: string;
  type?: string;
  filename: string;
};

export type DseOutboundResponseSummary = {
  mottakId: string;
  status?: string;
  oppdatert?: string;
  avvistsaarsak?: string;
  kind?: DseResponseKind;
  documents?: DseVedtakDocument[];
  orgNo?: string;
};

export type DseStatusResponse = {
  messageId: string;
  mottakId: string;
  status: DseStatus;
  response?: DseOutboundResponseSummary;
};

export const parseDseSubmitAck = (brResultData: string | undefined): DseSubmitAck | undefined => {
  if (!brResultData) return undefined;
  try {
    const parsed = JSON.parse(brResultData) as DseSubmitAck;
    return parsed.messageId && parsed.mottakId ? parsed : undefined;
  } catch {
    return undefined;
  }
};

export type ApplyFollowUp = {
  type: FollowUpType.apply;
  isRightsFromSubscription: boolean;
};

export type AttachableDocumentType = {
  documentType: DocumentType;
  customDocumentType: CustomDocumentType;
  requiresSigning: boolean;
};

export function canApplyDecision(followUps: FollowUp[]) {
  return followUps.some((followUp) => followUp.data.type === FollowUpType.apply);
}

export function isDecisionApplied(followUps: FollowUp[]) {
  return followUps.some(
    (followUp) =>
      followUp.data.type === FollowUpType.apply && followUp.status === FollowUpStatus.COMPLETED,
  );
}

export enum AttachmentType {
  // noinspection JSUnusedGlobalSymbols
  CONFIRMATION_SHARE_CAPITAL_CONTRIBUTIONS = "CONFIRMATION_SHARE_CAPITAL_CONTRIBUTIONS",
  CONFIRMATION_SHARE_CAPITAL_NON_CASH_CONTRIBUTIONS = "CONFIRMATION_SHARE_CAPITAL_NON_CASH_CONTRIBUTIONS",
  CONFIRMATION_SHARE_CAPITAL_DEBT_CONTRIBUTION = "CONFIRMATION_SHARE_CAPITAL_DEBT_CONTRIBUTION",
  CONFIRMATION_SHARE_CAPITAL_KIND_CONTRIBUTION = "CONFIRMATION_SHARE_CAPITAL_KIND_CONTRIBUTION",
  CONFIRMATION_SHARE_CAPITAL_AUDITOR = "CONFIRMATION_SHARE_CAPITAL_AUDITOR",
  CONFIRMATION_SHARE_CAPITAL_REDUCTION = "CONFIRMATION_SHARE_CAPITAL_REDUCTION",
  BOARD_MEMBERS_ELECTION_IDENTIFICATIONS = "BOARD_MEMBERS_ELECTION_IDENTIFICATIONS",
  EMPLOY_CEO_IDENTIFICATION = "EMPLOY_CEO_IDENTIFICATION",
  PROCURA_BY_NAME_OTHERS_IDENTIFICATION = "PROCURA_BY_NAME_OTHERS_IDENTIFICATION",
  POA = "POA",
  ATTACHMENT_IN_DECISION = "ATTACHMENT_IN_DECISION",
  UPLOADED_FILE = "UPLOADED_FILE",
  BR_STATUS = "BR_STATUS",
  TRANSACTIONLOG_ATTACHMENT = "TRANSACTIONLOG_ATTACHMENT",
}

export function requiredAttachmentLabel(t: AttachmentType): TMessage | undefined {
  switch (t) {
    case AttachmentType.CONFIRMATION_SHARE_CAPITAL_CONTRIBUTIONS:
      return { key: "dbot.requiredAttachments.CONFIRMATION_SHARE_CAPITAL_CONTRIBUTIONS" };
    case AttachmentType.CONFIRMATION_SHARE_CAPITAL_NON_CASH_CONTRIBUTIONS:
      return { key: "dbot.requiredAttachments.CONFIRMATION_SHARE_CAPITAL_NON_CASH_CONTRIBUTIONS" };
    case AttachmentType.CONFIRMATION_SHARE_CAPITAL_DEBT_CONTRIBUTION:
      return { key: "dbot.requiredAttachments.CONFIRMATION_SHARE_CAPITAL_DEBT_CONTRIBUTION" };
    case AttachmentType.CONFIRMATION_SHARE_CAPITAL_KIND_CONTRIBUTION:
      return { key: "dbot.requiredAttachments.CONFIRMATION_SHARE_CAPITAL_KIND_CONTRIBUTION" };
    case AttachmentType.CONFIRMATION_SHARE_CAPITAL_AUDITOR:
      return { key: "dbot.requiredAttachments.CONFIRMATION_SHARE_CAPITAL_AUDITOR" };
    case AttachmentType.CONFIRMATION_SHARE_CAPITAL_REDUCTION:
      return { key: "dbot.requiredAttachments.CONFIRMATION_SHARE_CAPITAL_REDUCTION" };
    case AttachmentType.BOARD_MEMBERS_ELECTION_IDENTIFICATIONS:
      return { key: "dbot.requiredAttachments.BOARD_MEMBERS_ELECTION_IDENTIFICATIONS" };
    case AttachmentType.EMPLOY_CEO_IDENTIFICATION:
      return { key: "dbot.requiredAttachments.EMPLOY_CEO_IDENTIFICATION" };
    case AttachmentType.PROCURA_BY_NAME_OTHERS_IDENTIFICATION:
      return { key: "dbot.requiredAttachments.PROCURA_BY_NAME_OTHERS_IDENTIFICATION" };
  }
}

export function isIdentificationAttachment(attachmentType: AttachmentType) {
  switch (attachmentType) {
    case AttachmentType.BOARD_MEMBERS_ELECTION_IDENTIFICATIONS:
    case AttachmentType.EMPLOY_CEO_IDENTIFICATION:
    case AttachmentType.PROCURA_BY_NAME_OTHERS_IDENTIFICATION:
      return true;
    default:
      return false;
  }
}

export type DecisionAttachment = {
  id: string;
  decisionId: string;
  type: AttachmentType;
  name: string;
};

export type NumberOfSharesForShareType = {
  shareTypeId: number;
  shareTypeCode?: ShareTypeCode;
  numberOfShares: number;
  paidInSharePremium?: number;
  paidInShareCapital?: number;
};

export type SharesPerShareType = {
  shareType: ShareType;
  numberOfShares: number;
};

export type SharesForShareholder = {
  shareholderId?: number;
  subscriberId?: string;
  shareTypeId: number;
  shareTypeCode?: ShareTypeCode;
  numberOfShares: number;
};

export const calculateOwnershipInPercent = (
  shares: SharesForShareholder[],
  shareholderId: number,
): number => {
  const total = sumBy(shares, "numberOfShares");
  const totalForShareholder = sumBy(
    shares.filter((s) => s.shareholderId === shareholderId),
    "numberOfShares",
  );
  return totalForShareholder / total;
};

export function ensureShareTypeCodeInSharesForShareTypes(
  shareTypes: ShareType[],
  shares?: NumberOfSharesForShareType[],
): NumberOfSharesForShareType[] {
  return (
    shares?.map((share) => {
      return {
        shareTypeId: share.shareTypeId,
        shareTypeCode:
          share.shareTypeCode ??
          shareTypes.find((shareType) => shareType.sharetypeId === share.shareTypeId)
            ?.shareTypeCode,
        numberOfShares: share.numberOfShares,
      };
    }) ?? []
  );
}

export function total(shares: NumberOfSharesForShareType[] | SharesForShareholder[] = []) {
  return shares.reduce((a, i) => a + i.numberOfShares, 0);
}

export function totalPaidInPremium(shares: NumberOfSharesForShareType[] = []): number | undefined {
  const values = shares.map((s) => s.paidInSharePremium).filter((v) => v !== undefined);
  if (values.length === 0) return undefined;
  return values.reduce((a, v) => a + v, 0);
}

export function totalPaidInShareCapital(
  shares: NumberOfSharesForShareType[] = [],
): number | undefined {
  const values = shares.map((s) => s.paidInShareCapital).filter((v) => v !== undefined);
  if (values.length === 0) return undefined;
  return values.reduce((a, v) => a + v, 0);
}

export function shareTypesWithShares(
  shareTypes: ShareType[],
  numberOfShares: NumberOfSharesForShareType[] = [],
) {
  return shareTypes.filter((shareType) =>
    numberOfShares.some(
      (shares) => shareType.sharetypeId === shares.shareTypeId && shares.numberOfShares > 0,
    ),
  );
}

export function numberOfSharesForShareType(
  shareType: ShareType,
  numberOfShares: NumberOfSharesForShareType[] = [],
) {
  return (
    numberOfShares.find((shares) => shareType.sharetypeId === shares.shareTypeId)?.numberOfShares ??
    0
  );
}

export function getNumberOfSharesForShareTypeExcludingOwnShares(
  shareType: ShareType,
  initialState: Evaluation,
) {
  const selfShareholder = getSelfShareholder(initialState.shareholders);
  const numberOfOwnShares = selfShareholder
    ? sharesForShareholderByShareType(
        selfShareholder,
        shareType,
        initialState.sharesForShareholders,
      )
    : 0;
  const totalShares = numberOfSharesForShareType(shareType, initialState.shareForClasses);
  return Big(totalShares).minus(numberOfOwnShares).toNumber();
}

export function getSelfShareholder(shareholders: ShareholderDTO[]) {
  return shareholders.find(
    (shareholder) => shareholder.birthDateOrOrganisationNumber === shareholder.organizationNumber,
  );
}

export function shareholdersWithShares(
  shareholders: ShareholderDTO[],
  sharesForShareholders: SharesForShareholder[] = [],
) {
  return shareholders.filter((shareholder) =>
    shareholderHasShares(shareholder, sharesForShareholders),
  );
}

export function shareholdersWithSharesExcludingSelf(
  shareholders: ShareholderDTO[],
  sharesForShareholders: SharesForShareholder[] = [],
) {
  return shareholders.filter(
    (shareholder) =>
      shareholder.birthDateOrOrganisationNumber !== shareholder.organizationNumber &&
      shareholderHasShares(shareholder, sharesForShareholders),
  );
}

export function shareholderHasShares(
  shareholder: ShareholderDTO,
  sharesForShareholders: SharesForShareholder[] = [],
) {
  return sharesForShareholders.some(
    (shares) => shareholder.id === shares.shareholderId && shares.numberOfShares > 0,
  );
}

export function shareholderHasSharesInShareType(
  shareholder: ShareholderDTO,
  shareType: ShareType,
  sharesForShareholders: SharesForShareholder[] = [],
) {
  return sharesForShareholders.some(
    (shares) =>
      shareholder.id === shares.shareholderId &&
      shareType.sharetypeId === shares.shareTypeId &&
      shares.numberOfShares > 0,
  );
}

export function getSharesForShareholders(
  shareholders: ShareholderDTO[],
  sharesForShareholders: SharesForShareholder[] = [],
) {
  return sharesForShareholders.filter((shares) =>
    shareholders.some((shareholder) => shareholder.id === shares.shareholderId),
  );
}

export function getSharesForShareholder(
  shareholder: ShareholderDTO,
  sharesForShareholders: SharesForShareholder[] = [],
) {
  return sharesForShareholders.filter(
    (shareForShareholder) =>
      shareForShareholder.shareholderId === shareholder.id &&
      shareForShareholder.numberOfShares > 0,
  );
}

export function sharesForShareholderByShareType(
  shareholder: ShareholderDTO,
  shareType: ShareType,
  sharesForShareholders: SharesForShareholder[] = [],
) {
  return getSharesForShareholder(shareholder, sharesForShareholders)
    .filter((shareForShareholder) => shareForShareholder.shareTypeId === shareType.sharetypeId)
    .reduce((acc, val) => acc + val.numberOfShares, 0);
}

export function sharesForShareholderByShareTypeCode(
  shareholder: ShareholderDTO,
  shareTypeCode: ShareTypeCode,
  sharesForShareholders: SharesForShareholder[] = [],
) {
  return getSharesForShareholder(shareholder, sharesForShareholders)
    .filter((shareForShareholder) => shareForShareholder.shareTypeCode === shareTypeCode)
    .reduce((acc, val) => acc + val.numberOfShares, 0);
}

export type Evaluation = {
  shareNominalValue: number;
  shareForClasses: NumberOfSharesForShareType[];
  articlesOfAssociation: ArticlesOfAssociation;
  company: Company;
  shareTypes: ShareType[];
  sharesForShareholders: SharesForShareholder[];
  roles: CompanyRole[];
  shareholders: ShareholderDTO[];
  currentAccountant?: AccountantListItem | null;
  currentBusinessManager?: BusinessManagerListItem | null;
  currentAuditor?: AuditorListItem;
};

export type InputState = {
  decisionId: string;
  numberOfShares: NumberOfSharesForShareType[];
  sharesForShareholders: SharesForShareholder[];
  sharenominalValue: number;
  auditorOrgNo: string;
  articles?: ArticlesOfAssociation;
  duplicateEntryIds?: string[];
};

export type ProjectInitializationParams = {
  user: User;
  currentSubscription: PaidSubscription | undefined;
  fileUploadConfig: FileUploadConfig;
  aiClient: AIClient;
  rootDispatch: EventDispatcher;
  rootStoreData: RootStoreData;
  companies: ProjectCompany[];
  featureToggles: FeatureToggles;
  enterpriseAccountInfo?: EnterpriseAccountInfo;
};

export type RecipientId = number | string;

export type Recipient = {
  id: RecipientId;
  name: string;
  email: string;
  att?: string;
  ref?: string;
  orgNo?: string;
  roleType?: string;
};

export type DynamicRecipientType = "debtConverters";

export const dynamicRecipientLabels: { [key in DynamicRecipientType]: TMessageKey } = {
  ["debtConverters"]: "debtConverters.title",
};

export type DynamicRecipient = {
  type: DynamicRecipientType;
  orgNo: string;
};

export type Subscriber = {
  shareholderId: number;
  subscriberId: string;
  name: string;
  email: string;
  isDebtConverter: boolean;
};

export interface ProjectContext {
  user: User;
  currentSubscription: PaidSubscription | undefined;
  fileUploadConfig: FileUploadConfig;
  currentDecisions: DecisionItem[];
  currentDocuments: DocumentItem[];
  documentStatuses: ProjectDocumentStatus[];
  documentLanguages: Language[];
  contextData: {
    [orgNo: string]: ProjectContextData;
  };
  initialState: Evaluation;
  inputStates: InputState[];
  isLocked: boolean;
  boardMeetingFields: BoardMeetingFields;
  generalMeetingFields: GeneralMeetingFields;
  orgNo: string;
  projectId: string;
  projectStatus: ProjectStatus;
  subscriptionTypeModel: SubscriptionTypeModel;
  boardMeetingNoticeRecipients: Recipient[];
  signingBoardMembers: CompanyRoleIdWithName[];
  aiClient: AIClient;
  isSharepointEnabled: boolean;
  locale: Language;
  detailsPanelMaximized: boolean;
  boardMeetingVotes: BoardMeetingVote[];
  validationErrors?: TMessage[];
  displayMode: "normal" | "fullscreen" | "screen-share" | "overlay";
  participationList: GeneralMeetingParticipation[];
  powerOfAttorneys: PowerOfAttorneyForGeneralMeeting[];
  availableCompanies: ProjectCompany[];
  companies: ProjectCompany[];
  isBatchProject: boolean;
  documentSigners: Map<string, Result<GetProjectDocumentSignersResponse>>;
  userPrefs: UserPrefs;
  state: ProjectControllerState;
  jsonData: ProjectJsonData;
  isProd: boolean;
  featureToggles: FeatureToggles;
  subscribers: Subscriber[];
  aoaMetadata?: AoAMetadata;
  dissolutionStatus?: DissolutionStatus;
  getUploadingDocumentBlob: (
    documentId: string,
    orgNo: string,
  ) => { filename: string; progress: number; isConverting: boolean } | undefined;
}

export interface DecisionContext<TData> extends ProjectContext {
  decisionId: string;
  decisionData: TData;
  inputState?: InputState;
  decisionTexts: LocalizedTextForDocument[];
  decisionIntroTexts: LocalizedTextForDocument[];
  attachments: ProjectDecisionAttachment[];
  isMainCompany: boolean;
}

export interface DocumentContext<T, TOverrides = DocumentOverrides> extends ProjectContext {
  documentId: string;
  documentData: T;
  documentOverrides: { [orgNo: string]: TOverrides | undefined };
  isProjectLocked: boolean;
  attachments: UploadedAttachmentProps[];
  autoAttachments: AttachmentPreview[];
}

export interface DecisionTypeHandler<
  T extends DecisionType,
  TData extends WithDecisionType<T>,
  TForm extends WithDecisionType<T>,
> {
  type: T;
  initNewForm: (
    dispatch: DbotEventDispatcher,
    context: DecisionContext<TData>,
    data?: TData,
    decision?: Decision,
  ) => TForm;
  initForm?: (form: TForm) => CleanupFunction | undefined;
  isValid: (form: TForm) => boolean;
  validate: (form: TForm) => boolean;
  applyForm: (form: TForm) => TData;
  handleEvent?: (form: TForm, event: DbotEvent) => void;
}

///
/// Document
///

export type DocumentItem = {
  documentId: string;
  type: DocumentType;
  title: TMessage;
  languages?: Language[];
  documentData?: DocumentData;
  hasValidationError: boolean;
};

export type WithDocumentType<T extends DocumentType> = {
  type: T;
};

export type DocumentTypeHandler<
  T extends DocumentType,
  TData extends WithDocumentType<T>,
  TForm extends WithDocumentType<T>,
  TOverrides = DocumentOverrides,
> = {
  type: T;
  initNewForm: (
    dispatch: DbotEventDispatcher,
    context: DocumentContext<TData>,
    options?: { justAdded?: boolean },
  ) => TForm;
  initForm: (form: TForm) => CleanupFunction | void;
  applyForm: (form: TForm) => TData;
  applyOverrides?: (form: TForm, orgNo: string) => TOverrides | undefined;
  handleEvent?: (form: TForm, event: DbotEvent) => void;
  getTitle?: (form: TForm, lang: Language) => TMessage | undefined;
  getRecipients?: (form: TForm, orgNo: string) => Recipient[];
  hasRecipientPreviews?: (form: TForm) => boolean;
  validate?: (form: TForm) => boolean;
};

export type CompanyRoleIdWithName = {
  companyroleId: number;
  name: string;
};

export const toCompanyRoleIdWithName = (role: CompanyRole) => ({
  companyroleId: role.companyroleId,
  name: getFullName(role),
});

export type GetDocumentViewingStatusResponse = {
  statusForViewers: DocumentViewingStatus[];
};

export type DocumentViewingStatus = {
  recipientName: string;
  recipientEmail: string;
  emailStatus?: EmailStatus;
  sendingTime?: string;
  viewedTime?: string;
  companyroleId?: CompanyroleId;
  boardMeetingId?: string;
  shareholderId?: number;
  recipientDocumentId: string;
};

export type IncorporationDocumentPart =
  | "BUSINESS_INFORMATION"
  | "CONFIRMATION_OF_PAID_SHARE_CAPITAL_CONTRIBUTION"
  | "CONFIRMATION_OF_AUDITOR_ROLE"
  | "CONFIRMATION_OF_ACCOUNTANT_ROLE"
  | "AOA";

export type ProjectDocumentStatus = {
  documentId: string;
  signingStatus: DocumentSigningStatus;
  numberOfSigners: number;
  numberOfSignatures: number;
  numberOfSignedDocuments: number;
  numberOfViewers: number;
  numberOfViewed: number;
  viewerRecipientIds: string[];
  sentAt?: string;
  signedAt?: string;
  isLocked?: boolean;
  incorporationDocumentPart?: IncorporationDocumentPart;
  lockedJsonData?: ProjectJsonData;
  authMethod?: MeetingAuthMethod;
  meetingPassword?: string;
  eventLogs: EventLog[];
};

export type SendDocumentsForViewingRequest = {
  organizationnumber: string;
  projectId: string;
  documentTypes: DocumentType[];
  documentId?: string;
  emailBody: string;
  authMethod?: MeetingAuthMethod;
  password?: string;
  recipients?: Recipient[];
};

export type MeetingAuthMethod = "LOGIN" | "PASSWORD";

export const sendDocumentsForViewingRequest = (
  data: SendDocumentsForViewingRequest,
): SendDocumentsForViewingRequest => data;

export type ResendNoticeOfBoardMeetingRequest = {
  organizationnumber: string;
  projectId: string;
  emailBody: string;
};
export const resendNoticeOfBoardMeetingRequest = (
  data: ResendNoticeOfBoardMeetingRequest,
): ResendNoticeOfBoardMeetingRequest => data;

///
/// Summary
///

export type SendDocumentsForSigningRequest = {
  projectId: string;
  documentId: string;
  emailBody?: string;
  signingSettings?: SigningSettings;
  signers: {
    reference?: string;
    email: string;
    phoneNumber?: string;
    signerName?: string;
    signingMethod: SigningMethod;
    language: Language;
    orgNo?: string;
  }[];
};

export function mapToSendForSigningRequest(
  projectId: string,
  event: SendDocumentForSigningEvent | SendSubscriptionFormForSigningEvent,
  signingSettings?: SigningSettings,
  emailBody?: string,
): SendDocumentsForSigningRequest {
  return {
    projectId,
    documentId: event.document.documentId,
    signingSettings,
    emailBody: emailBody,
    signers: event.signers.map(mapToRequestSigner),
  };
}

function mapToRequestSigner(s: Signer) {
  return {
    reference: s.reference,
    email: s.email,
    phoneNumber: s.phoneNumber ? formatPhoneNumberToE164(s.phoneNumber) : undefined,
    signerName: s.name,
    signingMethod: s.signingMethod,
    signerType: s.signerType,
    language: s.language,
    orgNo: s.orgNo,
  };
}

export type SignableDocument = {
  documentId: string;
  documentType: DocumentType;
  isCustomDocument: boolean;
  hasUnsupportedAttachments: boolean;
  attachments: UploadedAttachment[];
  previews: ProjectDocumentPreview[];
  recipients: Signer[];
};

export type GetSignableDocumentsResponse = {
  documents: SignableDocument[];
  companies: CompanyItem[];
};

export type DocumentSigners = {
  documentId: string;
  signers: Signer[];
  excludedAttachmentIds?: string[];
};

export type SendMultipleDocumentsForSigningRequest = {
  projectId: string;
  emailBody?: string;
  signingSettings?: SigningSettings;
  documents: {
    documentId: string;
    signers: ReturnType<typeof mapToRequestSigner>[];
    excludedAttachmentIds?: string[];
  }[];
};

export function mapToSendMultipleForSigningRequest(
  projectId: string,
  documents: DocumentSigners[],
  signingSettings: SigningSettings,
  emailBody: string,
): SendMultipleDocumentsForSigningRequest {
  return {
    projectId,
    signingSettings,
    emailBody,
    documents: documents.map((d) => ({
      documentId: d.documentId,
      signers: d.signers.map(mapToRequestSigner),
      excludedAttachmentIds: d.excludedAttachmentIds,
    })),
  };
}

///
/// Autosuggesting AoA paragraph number
///

export type SectionCategory =
  | "COMPANYNAME"
  | "SIGNATURE"
  | "BOARD"
  | "SHARE_CAPITAL"
  | "BUSINESS_PURPOSE";

export function extractSectionWithMaxScoreFromArticles(
  items: ExploreArticlesOfAssociationSearchResultItem[],
  categoryName: SectionCategory,
): number | undefined {
  const item = items.find((i) => i.category === categoryName);
  const max = maxBy(item?.articles, (a) => a.score);
  return max?.clause;
}

export type RemovalMode = RemoveParagraph | UpdateParagraph;

export type RemoveParagraph = {
  type: "RemoveParagraph";
};

export type UpdateParagraph = {
  type: "UpdateParagraph";
  newTexts: LocalizedText[];
};

export type ArticlesOfAssociationChangeMode = RemoveArticle | EditArticle | AddArticle;

export type RemoveArticle = {
  type: ArticleChangeType.RemoveArticle;
  id: string;
  paragraph: number;
  titlesBefore: LocalizedText[];
  textsBefore: LocalizedText[];
};

export type EditArticle = {
  type: ArticleChangeType.EditArticle;
  id: string;
  paragraph: number;
  titlesBefore: LocalizedText[];
  textsBefore: LocalizedText[];
  titlesAfter: LocalizedText[];
  textsAfter: LocalizedText[];
};

export type AddArticle = {
  type: ArticleChangeType.AddArticle;
  id: string;
  paragraph: number;
  titlesAfter: LocalizedText[];
  textsAfter: LocalizedText[];
};

///
/// Auditor
///

export type AuditorListItem = {
  name: string;
  organisationNumber: string;
  businessAddress1?: string;
  businessAddress2?: string;
  businessZip?: string;
  businessCity?: string;
  businessCountry?: string;
};

///
/// Accountant
///

export type AccountantListItem = {
  name: string;
  organisationNumber: string;
  businessAddress1?: string;
  businessAddress2?: string;
  businessZip?: string;
  businessCity?: string;
  businessCountry?: string;
};

///
/// Business manager (forretningsfører)
///

export type BusinessManagerListItem = {
  name: string;
  organisationNumber: string;
  businessAddress1?: string;
  businessAddress2?: string;
  businessZip?: string;
  businessCity?: string;
  businessCountry?: string;
};

///
///  ExploreArticlesOfAssociation
///

export type ExploreArticlesOfAssociationSearchResult = {
  results: ExploreArticlesOfAssociationSearchResultItem[];
};

//is this a good naming? Probably not! Hence, the JS-doc here.
//we want to search for specific words in the Articles of Association
//and suggest the most pertinent article / chapter to the user.
export type ExploreArticlesOfAssociationSearchResultItem = {
  category: string;
  articles: Array<ClauseScore>;
};

export type ClauseScore = {
  clause: number;
  score: number;
};

///
/// FetchIncreaseOfShareCapitalProperties
///

export type IncreaseOfShareCapitalSubscribersStatus =
  | IncreaseOfShareCapitalSubscribersStatusForNominalValue
  | IncreaseOfShareCapitalSubscribersStatusForIssue;

export type IncreaseOfShareCapitalSubscribersStatusForNominalValue = {
  type: IncreaseOfShareCapitalType.increaseNominalValue;
  contributionPerShareType: ContributionPerShareType[];
  subscribers: SubscriberForIncreaseNominalValue[];
};

export type IncreaseOfShareCapitalSubscribersStatusForIssue = {
  type: IncreaseOfShareCapitalType.shareIssue;
  selectedShareTypes: ShareIssuePerShareType[];
  subscribers: SubscriberForIssue[];
  isPreferentialRights: boolean;
  isOverSubscriptionAllowed: boolean;
  subscriptionPeriod: PeriodForSubscription;
};

export type ShareAllocationRoundsResponse = {
  allocationRounds: ShareAllocationRound[];
  isAllocationCompleted: boolean;
};

export type ShareAllocationRound = {
  id: string;
  projectId: string;
  documentId: string;
  sharetypeId?: number;
  shareTypeCode: ShareTypeCode;
  roundNumber: number;
  minShares: number;
  maxShares: number;
  allocations: ShareAllocation[];
  isCompleted: boolean;
};

export type ShareAllocation = {
  id: string;
  subscriberId: string;
  shareholderId?: number;
  name: string;
  offeredShares: number;
  subscribedShares?: number;
  allocatedShares?: number;
  subscriptionPrice: number;
};

///
/// Financial
///

export const defaultFinancialYear = (): string => toYearISOString(plusYears(new Date(), -1));

///
/// General Meeting Voting
///

export type IndividualVote = "for" | "against" | "blank";

export type GeneralMeetingIndividualVote = {
  id: string;
  shareholderId: number;
  vote: IndividualVote;
};

export const voteLabel = (vote: IndividualVote): TMessage => {
  switch (vote) {
    case "for":
      return { key: "vote.for" };
    case "against":
      return { key: "vote.against" };
    case "blank":
      return { key: "vote.blank" };
  }
};

export const voteLabelUppercase = (vote: IndividualVote): TMessage => {
  switch (vote) {
    case "for":
      return { key: "vote.for.uppercase" };
    case "against":
      return { key: "vote.against.uppercase" };
    case "blank":
      return { key: "vote.blank.uppercase" };
  }
};

export type GeneralMeetingVotesResponse = {
  meetChair: GeneralMeetingIndividualVote[];
  approveNoticeAndAgenda: GeneralMeetingIndividualVote[];
  decisions: {
    [key: string]: GeneralMeetingIndividualVote[];
  };
};

///
/// Freetext
///

export type Freetext = {
  type: DecisionType.freetext;
  localizedTitles: LocalizedText[];
  requiresGeneralMeeting: boolean; // is used to include in the list of matters to be discussed in the GM
  freetextTypeForAllDocuments: FreetextType;
  customizedFreetextTypePerDocument: FreetextTypeForDocument[];
};

export const emptyFreetext: Freetext = {
  type: DecisionType.freetext,
  localizedTitles: [],
  freetextTypeForAllDocuments: FreetextType.INFORMATION_MATTER,
  customizedFreetextTypePerDocument: [],
  requiresGeneralMeeting: false,
};

///
/// Project controller state
///

export type DeleteProjectState = {
  type: "delete-project";
  deleteResult?: Result;
  alert?: AlertMessage;
  onSuccess?: (DbotEvent | DCEvent)[];
};

export type ReadyState = { type: "ready" };

export type RemoveDecisionState = {
  type: "removeDecision";
  decisionId: string;
  onSuccess?: DbotEvent;
};

export type RemoveDocumentState = {
  type: "removeDocument";
  documentId: string;
  isSubmitting?: boolean;
};

export type LoadingState = { type: "loading"; message: TMessage };

export type RemovingDecisionState = {
  type: "removingDecision";
  decisionId: string;
  result: Result;
  onSuccess?: DbotEvent;
};

export type SelectBatchCompaniesState = {
  type: "select-batch-companies";
  options: ProjectCompany[];
  selectedCompanies: string[];
  filter?: string;
  result?: Result;
};

export type RemoveBatchCompanyState = {
  type: "remove-batch-company";
  orgNo: string;
  result?: Result;
};

export type AddFreetextState = {
  type: "add-freetext";
  input: string;
  documentId?: string;
  freetextType: CustomizedFreetextType;
  isLoading?: boolean;
};

export type DecisionSelectorState = {
  type: "decision-selector";
  selected: DecisionType[];
  documentId?: string;
  isLoading?: boolean;
};

export type CopyDecisionSelectorState = {
  type: "copy-decision-selector";
  projects: Result<ProjectForCopy[]>;
  selectedDecisions: string[];
  selectedProjectIndex: number;
  documentId?: string;
  isLoading?: boolean;
};

export type AddDocumentState = {
  type: "add-document";
  documentOptions: DocumentOption[];
  isDisabled?: boolean;
  isLoading?: boolean;
};

export type UploadingAttachment = {
  id: string;
  filename: string;
  progress: number;
  reference: string;
  isConverting?: boolean;
  originalFileType?: string;
};

export type UploadingAttachmentsState = {
  type: "uploading-attachments";
  reference?: string;
  uploads: UploadingAttachment[];
};

export type DeletingAttachmentState = {
  type: "deleting-attachment";
  attachmentId: string;
  reference?: string;
};

export type ProjectControllerState =
  | AddDocumentState
  | AddFreetextState
  | DecisionSelectorState
  | CopyDecisionSelectorState
  | DeleteProjectState
  | DeletingAttachmentState
  | LoadingState
  | ReadyState
  | RemoveBatchCompanyState
  | RemoveDecisionState
  | RemoveDocumentState
  | RemovingDecisionState
  | SelectBatchCompaniesState
  | UploadingAttachmentsState;

export const readyState = (): ReadyState => ({ type: "ready" });

export const removeDecisionState = (
  decisionId: string,
  onSuccess?: DbotEvent,
): RemoveDecisionState => ({
  type: "removeDecision",
  decisionId,
  onSuccess,
});

export const isRemoveDecision = (state: ProjectControllerState): state is RemoveDecisionState =>
  state.type === "removeDecision";

export const removingDecisionState = (state: RemoveDecisionState): RemovingDecisionState => ({
  type: "removingDecision",
  decisionId: state.decisionId,
  result: pending(),
});

export const isRemovingDecision = (state: ProjectControllerState): state is RemovingDecisionState =>
  state.type === "removingDecision";

export const sortedCompanies = (companies: ProjectCompany[]) =>
  sortBy(companies, [(c) => !c.isMainCompany, "name"]);

export const selectBatchCompaniesState = (context: ProjectContext): SelectBatchCompaniesState => ({
  type: "select-batch-companies",
  options: orderBy(
    context.availableCompanies,
    [(x) => Boolean(x.isMainCompany), "name"],
    ["desc", "asc"],
  ),
  selectedCompanies: context.companies.map((x) => x.orgNo),
});

export const selectBatchCompaniesEvent = (context: ProjectContext): DbotEvent => ({
  type: "dbot/change-state",
  state: selectBatchCompaniesState(context),
});

export const selectBatchCompaniesActions = (
  state: SelectBatchCompaniesState,
): {
  submit: DbotEvent[];
  cancel: DbotEvent[];
  setFilter: (s: string) => DbotEvent[];
  toggleAll: (filteredOptions: ProjectCompany[]) => DbotEvent[];
} => ({
  submit: [
    {
      type: "dbot/update-state",
      update: (state) => {
        if (state.type === "select-batch-companies") {
          state.result = pending();
        }
      },
    },
    {
      type: "dbot/save-selected-batch-companies",
      companies: state.selectedCompanies,
      onSuccess: [
        { type: "dbot/change-state", state: readyState() },
        { type: "dbot/open-tab", tab: "companies" },
      ],
      onFailure: [
        { type: "dbot/change-state", state: readyState() },
        { type: "show-alert", messageType: "error", message: { key: "general-error" } },
      ],
    },
  ],
  cancel: [{ type: "dbot/change-state", state: readyState() }],
  setFilter: (s) => [{ type: "dbot/change-state", state: { ...state, filter: s } }],
  toggleAll: (filteredOptions) => {
    const filteredOrgNos = filteredOptions.map((x) => x.orgNo);
    const selectedOrgNos = filteredOrgNos.filter((x) => state.selectedCompanies.includes(x));
    const mainOrgNo = state.options.find((x) => x.isMainCompany)?.orgNo;
    return [
      {
        type: "dbot/change-state",
        state: {
          ...state,
          selectedCompanies:
            selectedOrgNos.length === filteredOrgNos.length
              ? state.selectedCompanies.filter(
                  (x) => x === mainOrgNo || !filteredOrgNos.includes(x),
                )
              : uniq([...state.selectedCompanies, ...filteredOrgNos]),
        },
      },
    ];
  },
});

export const prepSelectBatchCompaniesCheckboxGroup = (
  state: SelectBatchCompaniesState,
  dispatch: DbotEventDispatcher,
): CheckboxGroupProps<ProjectCompany> => ({
  options: orderlessFilter(state.options, (x) => x.name, state.filter),
  idFn: (x) => x.orgNo,
  labelFn: (x) => x.name,
  disabledFn: (x) => x.isMainCompany ?? false,
  checkedFn: (x) => state.selectedCompanies.includes(x.orgNo),
  onChange: (_, x, checked) =>
    dispatch([
      {
        type: "dbot/change-state",
        state: {
          ...state,
          selectedCompanies: toggleElement(state.selectedCompanies, x.orgNo, checked),
        },
      },
    ]),
});

export const removeBatchCompanyState = (orgNo: string): ProjectControllerState => ({
  type: "remove-batch-company",
  orgNo,
});

export const removeBatchCompanyActions = (
  state: RemoveBatchCompanyState,
  context: ProjectContext,
): { submit: DbotEvent[]; cancel: DbotEvent[] } => ({
  submit: [
    {
      type: "dbot/update-state",
      update: (state) => {
        if (state.type === "remove-batch-company") {
          state.result = pending();
        }
      },
    },
    {
      type: "dbot/save-selected-batch-companies",
      companies: without(
        context.companies.map((c) => c.orgNo),
        state.orgNo,
      ),
      onSuccess: [
        { type: "dbot/change-state", state: readyState() },
        { type: "dbot/close-details-panel" },
      ],
      onFailure: [
        { type: "dbot/change-state", state: readyState() },
        { type: "show-alert", messageType: "error", message: { key: "general-error" } },
      ],
    },
  ],
  cancel: [{ type: "dbot/change-state", state: readyState() }],
});

export const canAddBatchCompany = ({
  currentDecisions,
}: {
  currentDecisions: DecisionItem[];
}): boolean => currentDecisions.every((d) => enabledBatchDecisionTypes.includes(d.type));

export const addFreetextState = (data: Partial<AddFreetextState>): AddFreetextState => ({
  ...data,
  type: "add-freetext",
  input: data.input ?? "",
  freetextType: data.freetextType ?? CustomizedFreetextType.INFORMATION_MATTER,
});

export const addFreetextTypeOptions: {
  value: CustomizedFreetextType;
  label: TMessage;
  icon: IconName;
}[] = [
  {
    value: CustomizedFreetextType.INFORMATION_MATTER,
    label: { key: "freetext.informationMatter" },
    icon: "chats",
  },
  {
    value: CustomizedFreetextType.DECISION_MATTER,
    label: { key: "freetext.decisionMatter" },
    icon: "gavel",
  },
];

export const selectedFreetextTypeOption = (
  state: AddFreetextState,
): { value: CustomizedFreetextType; label: TMessage; icon: IconName } =>
  addFreetextTypeOptions.find(({ value }) => value === state.freetextType) ??
  addFreetextTypeOptions[0];

export const addFreetextActions = (
  state: AddFreetextState,
): {
  onChange: (event: ChangeEvent<HTMLInputElement>) => DbotEvent[];
  selectFreetextType: (option: { value: CustomizedFreetextType }) => DbotEvent[];
  submit?: DbotEvent[];
  cancel: DbotEvent[];
} => ({
  onChange: (event) => [
    {
      type: "dbot/change-state",
      state: addFreetextState({ ...state, input: event.target.value }),
    },
  ],
  selectFreetextType: ({ value }: { value: CustomizedFreetextType }) => [
    {
      type: "dbot/change-state",
      state: addFreetextState({ ...state, freetextType: value }),
    },
  ],
  submit: when(!state.isLoading && state.input?.trim(), (val): DbotEvent[] => [
    {
      type: "dbot/change-state",
      state: addFreetextState({ ...state, isLoading: true }),
    },
    {
      type: "dbot/add-decisions",
      decisions: [
        { decisionType: DecisionType.freetext, title: val, freetextType: state.freetextType },
      ],
      options: { documentId: state.documentId },
      onDone: [
        {
          type: "dbot/change-state",
          state: addFreetextState({ ...state, isLoading: false, input: "" }),
        },
      ],
    },
  ]),
  cancel: [{ type: "dbot/change-state", state: readyState() }],
});

export const decisionSelectorState = ({
  selected,
  documentId,
}: {
  selected?: DecisionType[];
  documentId?: string;
}): DecisionSelectorState => ({ type: "decision-selector", selected: selected ?? [], documentId });

export const prepDecisionSelectorViewState = (
  state: DecisionSelectorState,
  context: {
    isBatchProject: boolean;
    orgNo: string;
    currentSubscription: PaidSubscription | undefined;
    currentDecisions: DecisionItem[];
    featureToggles: FeatureToggles;
  },
) => ({
  decisionsByCategory: getDecisionsByCategory({
    orgNo: context.orgNo,
    isBatchProject: context.isBatchProject,
    featureToggles: context.featureToggles,
    paidSubscription: when(context.currentSubscription, (subscription) => ({
      ...subscription,
      organisationNumber: context.orgNo,
    })),
    current: context.currentDecisions.map((d) => d.type).filter((d) => d !== DecisionType.freetext),
    checkedDecisions: state.selected,
  }),
});

export const decisionSelectorActions = (
  state: DecisionSelectorState,
): {
  toggle: (decisionType: DecisionType) => DbotEvent[];
  submit?: DbotEvent[];
  cancel: DbotEvent[];
} => ({
  toggle: (decisionType) => [
    {
      type: "dbot/change-state",
      state: {
        ...state,
        selected: state.selected.includes(decisionType)
          ? without(state.selected, decisionType)
          : [...state.selected, decisionType],
      },
    },
  ],
  submit:
    state.selected.length && !state.isLoading
      ? [
          { type: "dbot/change-state", state: { ...state, isLoading: true } },
          {
            type: "dbot/add-decisions",
            decisions: state.selected.map((decisionType) => ({ decisionType })),
            options: { documentId: state.documentId },
            onDone: [{ type: "dbot/change-state", state: readyState() }],
          },
        ]
      : undefined,
  cancel: [{ type: "dbot/change-state", state: readyState() }],
});

const copyDecisionProjectId = "copyDecisionsProjectId";

export function openCopyDecisionSelector(projectId: string, documentId?: string): DbotEvent[] {
  const selectedProjectId = sessionStorage.getItem(copyDecisionProjectId);
  const state = copyDecisionSelectorState({ documentId });
  return [
    {
      type: "dbot/change-state",
      state: state,
    },
    {
      type: "dbot/fetch-projects-for-copy",
      projectId,
      onSuccess: (projects) => {
        const selectedProjectIndex = projects.findIndex(
          (project) => project.projectId === selectedProjectId,
        );
        return compact([
          {
            type: "dbot/change-state",
            state: {
              ...state,
              projects: ok(projects),
              selectedProjectIndex: selectedProjectIndex >= 0 ? selectedProjectIndex : 0,
            },
          },
        ]);
      },
      onFailure: [
        {
          type: "dbot/change-state",
          state: { ...state, projects: failure() },
        },
      ],
    },
  ];
}

export const copyDecisionSelectorState = ({
  documentId,
}: {
  documentId?: string;
}): CopyDecisionSelectorState => ({
  type: "copy-decision-selector",
  selectedDecisions: [],
  selectedProjectIndex: 0,
  projects: pending(),
  documentId,
});

export function prepCopyDecisionSelectorViewState(
  state: CopyDecisionSelectorState,
  context: {
    projectId: string;
    isBatchProject: boolean;
    orgNo: string;
    currentSubscription: PaidSubscription | undefined;
    currentDecisions: DecisionItem[];
    featureToggles: FeatureToggles;
  },
) {
  const projects = isOk(state.projects) ? state.projects.data : [];
  const selectedProject = projects.length > 0 ? projects[state.selectedProjectIndex] : undefined;

  const enabledDecisionTypes = getEnabledDecisionTypes({
    isBatchProject: context.isBatchProject,
    featureToggles: context.featureToggles,
  });
  return {
    projects: projects,
    selectedProject: selectedProject,
    decisions:
      selectedProject?.decisions.map((decision) => {
        return {
          decision: decision,
          isSelected: state.selectedDecisions.includes(decision.decisionId),
          isDisabled: !enabledDecisionTypes.includes(decision.decisionType),
          isExisting: context.currentDecisions.some(
            (current) =>
              current.type === decision.decisionType &&
              decision.decisionType !== DecisionType.freetext,
          ),
        };
      }) ?? [],
  };
}

export const copyDecisionSelectorActions = (
  state: CopyDecisionSelectorState,
): {
  toggle: (decisionId: string) => DbotEvent[];
  toggleAll: (decisions: string[]) => DbotEvent[];
  setSelectedProject: (index: number) => DbotEvent[];
  submit?: DbotEvent[];
  cancel: DbotEvent[];
} => ({
  toggle: (decisionId) => [
    {
      type: "dbot/change-state",
      state: {
        ...state,
        selectedDecisions: state.selectedDecisions.includes(decisionId)
          ? without(state.selectedDecisions, decisionId)
          : [...state.selectedDecisions, decisionId],
      },
    },
  ],
  toggleAll: (decisions) => [
    {
      type: "dbot/change-state",
      state: {
        ...state,
        selectedDecisions: decisions,
      },
    },
  ],
  setSelectedProject: (index) => {
    return [
      {
        type: "dbot/change-state",
        state: {
          ...state,
          selectedProjectIndex: index,
        },
      },
    ];
  },
  submit:
    state.selectedDecisions.length &&
    isOk(state.projects) &&
    state.projects.data.length > state.selectedProjectIndex &&
    !state.isLoading
      ? [
          { type: "dbot/change-state", state: { ...state, isLoading: true } },
          {
            type: "dbot/copy-decisions",
            projectId: state.projects.data[state.selectedProjectIndex].projectId,
            decisions: state.selectedDecisions,
            options: { documentId: state.documentId },
            onDone: [
              { type: "dbot/change-state", state: readyState() },
              {
                type: "update-session-storage",
                key: copyDecisionProjectId,
                val: state.projects.data[state.selectedProjectIndex].projectId,
              },
            ],
          },
        ]
      : undefined,
  cancel: [{ type: "dbot/change-state", state: readyState() }],
});

export type DocumentOption = {
  type: DocumentType;
  label: TMessage;
  tag?: TagProps;
  options?: {
    customDocumentType?: CustomDocumentType;
    onSuccess?: (result: { addedDocumentIds: string[] }) => DbotEvent[];
  };
};

export function getDocumentOptions(
  currentDocuments: DocumentType[],
  isBatchProject: boolean,
): DocumentOption[] {
  return compact([
    !isBatchProject && DocumentType.notice_board_meeting,
    DocumentType.board_meeting,
    !isBatchProject && DocumentType.notice_general_meeting,
    DocumentType.general_meeting,
    DocumentType.articlesofassociation,
  ])
    .filter((t) => !currentDocuments.includes(t))
    .map((t) => ({ type: t, label: getDocumentLabel(t) }));
}

export const onCustomDocumentCreated: NonNullable<
  NonNullable<DocumentOption["options"]>["onSuccess"]
> = ({ addedDocumentIds }) => [
  {
    type: "dbot/open-details-panel",
    panel: { type: "custom", id: "document-settings", documentId: addedDocumentIds[0] },
  },
];

export function getCustomDocumentOptions(): DocumentOption[] | undefined {
  return compact([
    {
      type: DocumentType.custom_document,
      label: getDocumentLabel(DocumentType.custom_document),
      options: { onSuccess: onCustomDocumentCreated },
    },
    {
      type: DocumentType.custom_document,
      label: { key: "common.pdf" },
      options: { onSuccess: onCustomDocumentCreated, customDocumentType: "blob" },
    },
  ]);
}

export function isIncorporationDocument(document: DocumentItem): boolean {
  return (
    document.documentData?.type === DocumentType.custom_document &&
    document.documentData.customDocumentType === "incorporation"
  );
}

export const projectTemplateOptions = (
  access: ProjectTemplateAccess | undefined,
  orgNo: string,
  companyId: string,
): DropdownMenuItemType[] =>
  compact<DropdownMenuItemType>([
    access?.canUseTemplate && {
      type: "item",
      label: { key: "dbot.use-a-template" },
      tag: { color: "neutral", label: access.enterpriseAccountName },
      onSelect: [thunk(() => prepToShowUseTemplateDialog(orgNo, companyId, access))],
      icon: "building",
    },
    ...(access ? manageTemplateOptions(access, orgNo) : []),
  ]);

const manageTemplateOptions = (
  access: ProjectTemplateAccess,
  orgNo: string,
): DropdownMenuItemType[] => [
  { type: "separator" },
  {
    type: "item",
    label: { key: "dbot.manage-templates" },
    icon: "gear",
    onSelect: [
      navigateTo(
        {
          name: "enterprise-account",
          params: {
            id: access.enterpriseAccountId,
            tab: "content",
            accountSettingsTab: templateTypeSegments[TemplateType.PROJECT],
          },
        },
        { state: navigationState({ referrer: adminProjects(orgNo) }) },
      ),
    ],
    isDisabled: !access.canManageTemplates,
    tooltip: !access.canManageTemplates
      ? {
          key: "dbot.manage-templates.tooltip",
          params: { accountName: access.enterpriseAccountName },
        }
      : undefined,
  },
];

const INCORPORATION_CONFIRMATION_FOLLOW_UP_TYPES = [
  FollowUpType.signBusinessInformation,
  FollowUpType.confirmationOfPaidShareCapitalContribution,
  FollowUpType.confirmationOfAuditorRole,
  FollowUpType.confirmationOfAccountantRole,
];

export function countIncorporationConfirmationDocuments(followUps: DecisionFollowUp[]): number {
  return followUps
    .flatMap((d) => d.followUps)
    .filter((fu) => INCORPORATION_CONFIRMATION_FOLLOW_UP_TYPES.includes(fu.data.type)).length;
}

export function canAddDocumentToProject(context: {
  currentDocuments: DocumentItem[];
  isLocked?: boolean;
}): boolean {
  return !context.currentDocuments.some(isIncorporationDocument);
}

export function canAddDecisionToProject(context: { currentDecisions: DecisionItem[] }): boolean {
  return !context.currentDecisions.some((d) => exclusiveDecisionTypes.has(d.type));
}

export const addDocumentState = (
  context: ProjectContext,
  data: { isLoading?: boolean } = {},
): AddDocumentState => {
  const documentOptions = canAddDocumentToProject(context)
    ? [
        ...getDocumentOptions(
          context.currentDocuments.map((x) => x.type),
          context.isBatchProject,
        ),
        ...(getCustomDocumentOptions() ?? []),
      ]
    : [];

  return {
    type: "add-document",
    documentOptions,
    isLoading: data.isLoading || (context.state.type === "add-document" && context.state.isLoading),
    isDisabled: !documentOptions.length,
  };
};

export type AddDocumentActions = {
  submit: (documentType: DocumentType, options?: DocumentOption["options"]) => DbotEvent[];
};

export const addDocumentActions = (context: ProjectContext): AddDocumentActions => ({
  submit: (documentType, options) =>
    compact([
      { type: "dbot/change-state", state: addDocumentState(context, { isLoading: true }) },
      {
        type: "dbot/add-documents",
        documentTypes: [documentType],
        customDocumentType: options?.customDocumentType,
        onDone: (err, result): DbotEvent[] => {
          const addedDocumentIds = result?.addedDocumentIds ?? [];
          return err
            ? [{ type: "dbot/change-state", state: readyState() }]
            : [
                { type: "dbot/change-state", state: readyState() },
                {
                  type: "dbot/toggle-document-panel",
                  documentId: addedDocumentIds[0],
                  expanded: true,
                },
                ...(options?.onSuccess?.({ addedDocumentIds }) ?? []),
              ];
        },
      },
    ]),
});

export const removeDocumentAction = (documentId: string): DbotEvent[] => [
  { type: "dbot/change-state", state: { type: "removeDocument", documentId } },
];

export const confirmRemoveDocumentDialogActions = (
  state: RemoveDocumentState,
): { onSubmit: DbotEvent[]; onCancel: DbotEvent[] } => ({
  onSubmit: [
    { type: "dbot/change-state", state: { ...state, isSubmitting: true } },
    {
      type: "dbot/confirm-remove-document",
      documentId: state.documentId,
      onSuccess: [{ type: "dbot/change-state", state: readyState() }],
    },
  ],
  onCancel: [{ type: "dbot/change-state", state: readyState() }],
});

///
/// Events
///

export type ChangeStateEvent = {
  type: "dbot/change-state";
  state: ProjectControllerState;
};

export type UpdateStateEvent = {
  type: "dbot/update-state";
  update: (state: ProjectControllerState) => void;
};

export type ReloadProjectEvent = {
  type: "dbot/reload-project";
};

export type RefreshProjectStatusEvent = {
  type: "dbot/refresh-project-status";
};

export type UpdateUploadingAttachmentPropsEvent = {
  type: "dbot/update-uploading-attachment-props";
  reference: UrnReference;
  filename: string;
  documentConversionId?: string;
  isConverting?: boolean;
  originalFileType?: string;
};

export type RefreshProjectStatusResultEvent = {
  type: "dbot/refresh-project-status-result";
  result: Ok | Failure<TMessage>;
};

export const refreshProjectStatusEvent = (): RefreshProjectStatusEvent => ({
  type: "dbot/refresh-project-status",
});

export const refreshProjectStatusSuccessEvent = (): RefreshProjectStatusResultEvent => ({
  type: "dbot/refresh-project-status-result",
  result: ok(undefined),
});

export const refreshProjectStatusFailureEvent = (
  error: TMessage,
): RefreshProjectStatusResultEvent => ({
  type: "dbot/refresh-project-status-result",
  result: failure(error),
});

export type ExploreArticlesOfAssociationEvent = {
  type: "dbot/explore-articles-of-association";
  articles?: ArticlesOfAssociation;
};
export type ExploreArticlesOfAssociationSuccessEvent = {
  type: "dbot/explore-articles-of-association-success";
  data: ExploreArticlesOfAssociationSearchResult;
};
export type ExploreArticlesOfAssociationErrorEvent = {
  type: "dbot/explore-articles-of-association-error";
  error: TMessage;
};

export type DecisionOption = {
  decisionType: DecisionType;
  title?: string;
  freetextType?: CustomizedFreetextType;
};

export type AddDecisionsEvent = {
  type: "dbot/add-decisions";
  decisions: DecisionOption[];
  options?: { index?: number; documentId?: string };
  onDone?: DbotEvent[];
};

export const addDecisionsEvent = (
  decisions: DecisionOption[],
  options?: { index?: number; documentId?: string },
): AddDecisionsEvent => ({ type: "dbot/add-decisions", decisions, options });

export type CopyDecisionsEvent = {
  type: "dbot/copy-decisions";
  projectId: string;
  decisions: string[];
  options?: { documentId?: string };
  onDone?: (DbotEvent | DCEvent)[];
};

export type AddDocumentsEvent = {
  type: "dbot/add-documents";
  documentTypes: DocumentType[];
  customDocumentType?: CustomDocumentType;
  customDocumentTitle?: string;
  customDocumentInitialRecipients?: CustomDocumentInitialRecipients;
  onDone?: (err: unknown, result?: { addedDocumentIds: string[] }) => (DCEvent | DbotEvent)[];
};

export type DocumentsAddedEvent = {
  type: "dbot/documents-added";
  addedDocumentTypes: DocumentType[];
  existingDocumentTypes: DocumentType[];
};

export const addGeneralMeetingDocuments = (): AddDocumentsEvent => ({
  type: "dbot/add-documents",
  documentTypes: [DocumentType.notice_general_meeting, DocumentType.general_meeting],
});

export const addSubscriptionFormDocument = (): AddDocumentsEvent => ({
  type: "dbot/add-documents",
  documentTypes: [DocumentType.subscription_form],
});

export type RemoveDocumentsEvent = {
  type: "dbot/remove-documents";
  documentTypes: DocumentType[];
};

export const removeSubscriptionFormDocument = (): RemoveDocumentsEvent => ({
  type: "dbot/remove-documents",
  documentTypes: [DocumentType.subscription_form],
});

export type RemoveDecisionEvent = {
  type: "dbot/remove-decision";
  decisionId: string;
  onSuccess?: DbotEvent;
};

export type RemoveCustomPanelEvent = {
  type: "dbot/remove-custom-panel";
  id: string;
};

export type UpdateInputStateEvent = {
  type: "dbot/update-input-state";
};

export const updateInputStateEvent = (): UpdateInputStateEvent => ({
  type: "dbot/update-input-state",
});

export type RegisterNewSignerEvent = {
  type: "dbot/register-new-signer";
  name: string;
  email?: string;
};

export type RegisterNewSignerResultEvent = {
  type: "dbot/register-new-signer-result";
  result: Ok<CompanyRole> | Failure<TMessage>;
};

export const registerNewSignerEvent = (name: string, email?: string): RegisterNewSignerEvent => ({
  type: "dbot/register-new-signer",
  name,
  email,
});

export const registerNewSignerSuccessEvent = (role: CompanyRole): RegisterNewSignerResultEvent => ({
  type: "dbot/register-new-signer-result",
  result: ok(role),
});

export const registerNewSignerFailureEvent = (message: TMessage): RegisterNewSignerResultEvent => ({
  type: "dbot/register-new-signer-result",
  result: failure(message),
});

export type AddIncreaseOfShareCapitalSubscribersForIssueEvent = {
  type: "dbot/addIncreaseOfShareCapitalSubscribersForIssue";
  subscribers: SubscriberForIssue[];
  signersForAllSubscribers: SubscriberSigner[];
};

export type AddIncreaseOfShareCapitalSubscribersForNominalValueEvent = {
  type: "dbot/addIncreaseOfShareCapitalSubscribersForNominalValue";
  subscribers: SubscriberForIncreaseNominalValue[];
  signersForAllSubscribers: SubscriberSigner[];
};

export type FetchIncreaseOfShareCapitalSubscribersAllocationEvent = {
  type: "dbot/fetch-increase-of-share-capital-subscribers-allocation";
};

export type FetchIncreaseOfShareCapitalSubscribersAllocationResultEvent = {
  type: "dbot/fetch-increase-of-share-capital-subscribers-allocation-result";
  result: Ok<ShareAllocationRoundsResponse> | Failure<TMessage>;
};

export const fetchIncreaseOfShareCapitalSubscribersAllocationSuccessEvent = (
  data: ShareAllocationRoundsResponse,
): FetchIncreaseOfShareCapitalSubscribersAllocationResultEvent => ({
  type: "dbot/fetch-increase-of-share-capital-subscribers-allocation-result",
  result: ok(data),
});

export const fetchIncreaseOfShareCapitalSubscribersAllocationFailureEvent = (
  error: TMessage,
): FetchIncreaseOfShareCapitalSubscribersAllocationResultEvent => ({
  type: "dbot/fetch-increase-of-share-capital-subscribers-allocation-result",
  result: failure(error),
});

export type SaveShareAllocationEvent = {
  type: "dbot/save-share-allocation";
  allocationRounds: ShareAllocationRound[];
  isAllocationCompleted: boolean;
};

export type SaveShareAllocationResultEvent = {
  type: "dbot/save-share-allocation-result";
  result: Ok<boolean> | Failure<TMessage>;
};

export const saveShareAllocationSuccessEvent = (
  isCompleted: boolean,
): SaveShareAllocationResultEvent => ({
  type: "dbot/save-share-allocation-result",
  result: ok(isCompleted),
});

export const saveShareAllocationFailureEvent = (
  error: TMessage,
): SaveShareAllocationResultEvent => ({
  type: "dbot/save-share-allocation-result",
  result: failure(error),
});

export type DownloadSignedDocumentEvent = {
  type: "dbot/download-signed-document";
  documentId: string;
  documentType: DocumentType;
};
export const downloadSignedDocumentEvent = (
  documentId: string,
  documentType: DocumentType,
): DownloadSignedDocumentEvent => ({
  type: "dbot/download-signed-document",
  documentId,
  documentType,
});

export type DownloadSignedSubscriptionForms = {
  type: "dbot/download-signed-subscription-forms";
  documentId: string;
};
export const downloadSignedSubscriptionForms = (
  documentId: string,
): DownloadSignedSubscriptionForms => ({
  type: "dbot/download-signed-subscription-forms",
  documentId,
});

export type ValidateProjectEvent = {
  type: "dbot/validate-project";
  contextDocumentId?: string;
  onSuccess: DbotEvent;
};

export type ReopenDocumentEvent = {
  type: "dbot/reopen-document";
  documentId: string;
  isCancelSignatureRequest?: boolean;
};

export const reopenDocumentEvent = (
  documentId: string,
  isCancelSignatureRequest?: boolean,
): ReopenDocumentEvent => ({
  type: "dbot/reopen-document",
  documentId,
  isCancelSignatureRequest,
});

export type ReopenDocumentResultEvent = {
  type: "dbot/reopen-document-result";
  documentId: string;
  label: string;
  reopenResult: Result;
};

export const reopenDocumentResultEvent = (
  documentId: string,
  result: Result,
): ReopenDocumentResultEvent => ({
  type: "dbot/reopen-document-result",
  documentId,
  label: "reopen.dialog.header",
  reopenResult: result,
});

export type GetSigningStatusEvent = {
  type: "dbot/get-signing-status";
  documentId: string;
};

export const getSigningStatusEvent = (documentId: string): GetSigningStatusEvent => ({
  type: "dbot/get-signing-status",
  documentId,
});

export type GetSigningStatusResultEvent = {
  type: "dbot/get-signing-status-result";
  documentId: string;
  result: Ok<GetDocumentSigningStatusResponse> | Failure<TMessage>;
};

export const getSigningStatusSuccessEvent = (
  documentId: string,
  data: GetDocumentSigningStatusResponse,
): GetSigningStatusResultEvent => ({
  type: "dbot/get-signing-status-result",
  documentId,
  result: ok(data),
});

export const getSigningStatusFailureEvent = (
  documentId: string,
  error: TMessage,
): GetSigningStatusResultEvent => ({
  type: "dbot/get-signing-status-result",
  documentId,
  result: failure(error),
});

export type GetSubscriptionStatusEvent = {
  type: "dbot/get-subscription-status";
  documentId: string;
};

export const getSubscriptionStatusEvent = (documentId: string): GetSubscriptionStatusEvent => ({
  type: "dbot/get-subscription-status",
  documentId,
});

export type GetSubscriptionStatusResultEvent = {
  type: "dbot/get-subscription-status-result";
  documentId: string;
  result: Ok<GetSubscriptionStatusResponse> | Failure<TMessage>;
};

export const getSubscriptionStatusSuccessEvent = (
  documentId: string,
  data: GetSubscriptionStatusResponse,
): GetSubscriptionStatusResultEvent => ({
  type: "dbot/get-subscription-status-result",
  documentId,
  result: ok(data),
});

export const getSubscriptionStatusFailureEvent = (
  documentId: string,
  error: TMessage,
): GetSubscriptionStatusResultEvent => ({
  type: "dbot/get-subscription-status-result",
  documentId,
  result: failure(error),
});

export type GetViewingStatusEvent = {
  type: "dbot/get-viewing-status";
  documentId: string;
};

export const getViewingStatusEvent = (documentId: string): GetViewingStatusEvent => ({
  type: "dbot/get-viewing-status",
  documentId,
});

export type GetViewingStatusResultEvent = {
  type: "dbot/get-viewing-status-result";
  documentId: string;
  result: Ok<GetDocumentViewingStatusResponse> | Failure<TMessage>;
};

export const getViewingStatusSuccessEvent = (
  documentId: string,
  data: GetDocumentViewingStatusResponse,
): GetViewingStatusResultEvent => ({
  type: "dbot/get-viewing-status-result",
  documentId,
  result: ok(data),
});

export const getViewingStatusFailureEvent = (
  documentId: string,
  error: TMessage,
): GetViewingStatusResultEvent => ({
  type: "dbot/get-viewing-status-result",
  documentId,
  result: failure(error),
});

export type AutosignDocumentEvent = {
  type: "dbot/autosign-document";
  document: DocumentItem;
  label: TMessage;
};

export type AutoapproveSendToBREvent = {
  type: "dbot/autoapprove-send-to-br";
  decisionId: string;
  label: TMessage;
};

export type ResendDocumentForViewing = {
  recipientName: string;
  recipientEmail: string;
  companyroleId?: number;
  boardMeetingId?: string;
  shareholderId?: number;
  recipientDocumentId: string;
};

export type ResendNoticeToUpdatedRecipientEvent = {
  type: "dbot/resend-notice-to-updated-recipient";
  documentId: string;
  data: ResendDocumentForViewing;
};

export type ResendNoticeToUpdatedRecipientResultEvent = {
  type: "dbot/resend-notice-to-updated-recipient-result";
  documentId: string;
  data: ResendDocumentForViewing;
  result: Ok | Failure<TMessage>;
};

export type SendDocumentForViewingEvent = {
  type: "dbot/send-document-for-viewing";
  documentId: string;
  emailBody: string;
  authMethod?: MeetingAuthMethod;
  password?: string;
  recipients?: Recipient[];
  onSuccess?: () => void;
  onFailure?: () => void;
  isResend?: boolean;
};

export const sendDocumentForViewingEvent = (
  document: DocumentItem,
  emailBody: string,
  authMethod?: MeetingAuthMethod,
  password?: string,
  isResend?: boolean,
): SendDocumentForViewingEvent => ({
  type: "dbot/send-document-for-viewing",
  documentId: document.documentId,
  emailBody,
  authMethod,
  password,
  isResend,
});

export type SendDocumentForViewingResultEvent = {
  type: "dbot/send-document-for-viewing-result";
  documentId: string;
  result: Ok | Failure<TMessage>;
  isResend?: boolean;
};

export const sendDocumentForViewingSuccessEvent = (
  documentId: string,
  isResend?: boolean,
): SendDocumentForViewingResultEvent => ({
  type: "dbot/send-document-for-viewing-result",
  documentId,
  result: ok(),
  isResend,
});

export const sendDocumentForViewingFailureEvent = (
  documentId: string,
  message: TMessage,
): SendDocumentForViewingResultEvent => ({
  type: "dbot/send-document-for-viewing-result",
  documentId,
  result: failure(message),
});

export type ResendNoticeOfBoardMeetingEvent = {
  type: "dbot/resend-notice-of-board-meeting";
  document: DocumentItem;
  emailBody: string;
};

export const resendNoticeOfBoardMeetingEvent = (
  document: DocumentItem,
  emailBody: string,
): ResendNoticeOfBoardMeetingEvent => ({
  type: "dbot/resend-notice-of-board-meeting",
  document,
  emailBody,
});

export type ResendNoticeOfBoardMeetingResultEvent = {
  type: "dbot/resend-notice-of-board-meeting-result";
  document: DocumentItem;
  result: Ok | Failure<TMessage>;
};

export const resendNoticeOfBoardMeetingSuccessEvent = (
  document: DocumentItem,
): ResendNoticeOfBoardMeetingResultEvent => ({
  type: "dbot/resend-notice-of-board-meeting-result",
  document,
  result: ok(),
});

export const resendNoticeOfBoardMeetingFailureEvent = (
  document: DocumentItem,
  message: TMessage,
): ResendNoticeOfBoardMeetingResultEvent => ({
  type: "dbot/resend-notice-of-board-meeting-result",
  document,
  result: failure(message),
});

export type SendSubscriptionFormForSigningEvent = {
  type: "dbot/send-subscription-form-for-signing";
  document: DocumentItem;
  signers: Signer[];
};

export const sendSubscriptionFormForSigningEvent = (
  document: DocumentItem,
  signers: Signer[],
): SendSubscriptionFormForSigningEvent => ({
  type: "dbot/send-subscription-form-for-signing",
  document,
  signers,
});

export type SendSubscriptionFormForSigningResultEvent = {
  type: "dbot/send-subscription-form-for-signing-result";
  document: DocumentItem;
  signers: Signer[];
  result: Ok | Failure;
};
export const sendSubscriptionFormForSigningResult = (
  request: SendSubscriptionFormForSigningEvent,
  result: Ok | Failure,
): SendSubscriptionFormForSigningResultEvent => ({
  ...request,
  type: "dbot/send-subscription-form-for-signing-result",
  result,
});

export type SendDocumentForSigningEvent = {
  type: "dbot/send-document-for-signing";
  document: DocumentItem;
  signers: Signer[];
  emailBody: string;
  signingSettings: SigningSettings;
};

export const sendDocumentForSigningEvent = (
  document: DocumentItem,
  signers: Signer[],
  emailBody: string,
  signingSettings: SigningSettings,
): SendDocumentForSigningEvent => ({
  type: "dbot/send-document-for-signing",
  document,
  signers,
  emailBody,
  signingSettings,
});

export type SendDocumentForSigningResultEvent = {
  type: "dbot/send-document-for-signing-result";
  document: DocumentItem;
  signers: Signer[];
  result: Ok<null> | Failure;
  emailBody: string;
  signingSettings: SigningSettings;
  userPrefs?: UserPrefs;
};

export const sendDocumentForSigningResultEvent = (
  request: SendDocumentForSigningEvent,
  result: Ok<null> | Failure,
  emailBody: string,
  userPrefs?: UserPrefs,
): SendDocumentForSigningResultEvent => ({
  ...request,
  type: "dbot/send-document-for-signing-result",
  emailBody,
  result,
  userPrefs,
});

export type SendMultipleDocumentsForSigningEvent = {
  type: "dbot/send-multiple-documents-for-signing";
  documents: DocumentSigners[];
  emailBody: string;
  signingSettings: SigningSettings;
};

export const sendMultipleDocumentsForSigningEvent = (
  documents: DocumentSigners[],
  emailBody: string,
  signingSettings: SigningSettings,
): SendMultipleDocumentsForSigningEvent => ({
  type: "dbot/send-multiple-documents-for-signing",
  documents,
  emailBody,
  signingSettings,
});

export type SendMultipleDocumentsForSigningResultEvent = {
  type: "dbot/send-multiple-documents-for-signing-result";
  result: Ok<null> | Failure;
};

export const sendMultipleDocumentsForSigningResultEvent = (
  result: Ok<null> | Failure,
): SendMultipleDocumentsForSigningResultEvent => ({
  type: "dbot/send-multiple-documents-for-signing-result",
  result,
});

export type ResendDocumentForSigningEvent = {
  type: "dbot/resend-document-for-signing";
  documentId: string;
  signedDocumentId: string;
  name: string;
  email: string;
  phoneNumber?: PhoneNumber;
  signingMethod: SigningMethod;
  language?: Language;
};

export type ResendDocumentForSigningResultEvent = {
  type: "dbot/resend-document-for-signing-result";
  documentId: string;
  signedDocumentId: string;
  result: Ok<GetDocumentSigningStatusResponse> | Failure<TMessage>;
};

export type UpdateBoardMeetingRecipientItem = {
  id: string | number;
  email: string;
};
export type UpdateBoardMeetingRecipientsEvent = {
  type: "dbot/update-board-meeting-recipients";
  items: UpdateBoardMeetingRecipientItem[];
};

export const updateBoardMeetingRecipientsEvent = (
  items: UpdateBoardMeetingRecipientItem[],
): UpdateBoardMeetingRecipientsEvent => ({
  type: "dbot/update-board-meeting-recipients",
  items,
});

export type UpdateBoardMeetingRecipientsResultEvent = {
  type: "dbot/update-board-meeting-recipients-result";
  result: Failure<TMessage> | Ok<UpdateBoardMeetingRecipientItem[]>;
};

export const updateBoardMeetingRecipientsSuccessEvent = (
  items: UpdateBoardMeetingRecipientItem[],
): UpdateBoardMeetingRecipientsResultEvent => ({
  type: "dbot/update-board-meeting-recipients-result",
  result: ok(items),
});

export const updateBoardMeetingRecipientsFailureEvent = (
  error: TMessage,
): UpdateBoardMeetingRecipientsResultEvent => ({
  type: "dbot/update-board-meeting-recipients-result",
  result: failure(error),
});

export type UpdateBoardMeetingParticipantsEvent = {
  type: "dbot/update-board-meeting-participants";
  participants: CompanyroleId[];
  extraParticipants: ExtraParticipant[];
};

export const updateBoardMeetingParticipantsEvent = (
  participants: CompanyroleId[],
  extraParticipants: ExtraParticipant[],
): UpdateBoardMeetingParticipantsEvent => ({
  type: "dbot/update-board-meeting-participants",
  participants,
  extraParticipants,
});

export type UpdateShareholderEmailEvent = {
  type: "dbot/update-shareholder-email";
  shareholderId: number;
  email: string;
  onFulfilled: (result: Result<TMessage, TMessage>, value?: string) => void;
};

export type UpdateShareholderEmailBatchEvent = {
  type: "dbot/update-shareholder-email-batch";
  updates: { shareholderId: number; email: string }[];
  onSuccess?: (res: UpdateShareholderEmailBatchResponse) => (DCEvent | DbotEvent)[];
  onFailure?: () => (DCEvent | DbotEvent)[];
};

export function applyShareholderEmail(
  context: ProjectContext,
  shareholderId: number,
  email: string,
) {
  for (const contextData of Object.values(context.contextData)) {
    const r = contextData.initialState.shareholders.find((r) => r.id === shareholderId);
    if (r) {
      r.email = email;
    }
  }
}

export type UpdateCompanyRoleItem = {
  companyroleId: number;
  email: string;
};

export type UpdateCompanyRoleEmailsEvent = {
  type: "dbot/update-company-role-emails";
  orgNo: string;
  items: UpdateCompanyRoleItem[];
};

export const updateCompanyRoleEmailsEvent = (
  orgNo: string,
  items: UpdateCompanyRoleItem[],
): UpdateCompanyRoleEmailsEvent => ({
  type: "dbot/update-company-role-emails",
  orgNo,
  items,
});

export type UpdateCompanyRoleEmailsResultEvent = {
  type: "dbot/update-company-role-emails-result";
  orgNo: string;
  items: UpdateCompanyRoleItem[];
  result: Ok<TMessage> | Failure<TMessage>;
};

export const updateCompanyRoleEmailsSuccessEvent = (
  orgNo: string,
  items: UpdateCompanyRoleItem[],
  message: TMessage,
): UpdateCompanyRoleEmailsResultEvent => ({
  type: "dbot/update-company-role-emails-result",
  orgNo,
  items,
  result: ok(message),
});

export const updateCompanyRoleEmailsFailureEvent = (
  orgNo: string,
  items: UpdateCompanyRoleItem[],
  message: TMessage,
): UpdateCompanyRoleEmailsResultEvent => ({
  type: "dbot/update-company-role-emails-result",
  orgNo,
  items,
  result: failure(message),
});

export function applyCompanyRoleEmails(context: ProjectContext, items: UpdateCompanyRoleItem[]) {
  for (const updated of items) {
    for (const contextData of Object.values(context.contextData)) {
      const r = contextData.initialState.roles.find(
        (r) => r.companyroleId === updated.companyroleId,
      );
      if (r) {
        r.email = updated.email;
      }
    }
  }
}

export type DecisionText = {
  documentType: DocumentType;
  language: Language;
  text: string;
  translationOption?: TranslationOption;
};

export type UpdateDecisionTextsEvent = {
  type: "dbot/update-decision-texts";
  decisionId: string;
  texts: DecisionText[];
};

export const updateDecisionText = (
  decisionId: string,
  documentType: DocumentType,
  language: Language,
  text: string | undefined,
  translationOption?: TranslationOption,
): UpdateDecisionTextsEvent => ({
  type: "dbot/update-decision-texts",
  decisionId,
  texts: [
    {
      documentType,
      language,
      translationOption,
      text: text ?? "",
    },
  ],
});

export type UpdateDecisionIntroTextsEvent = {
  type: "dbot/update-decision-intro-texts";
  decisionId: string;
  texts: DecisionText[];
};

export const updateDecisionIntroText = (
  decisionId: string,
  documentType: DocumentType,
  language: Language,
  text: string | undefined,
  translationOption?: TranslationOption,
): UpdateDecisionIntroTextsEvent => ({
  type: "dbot/update-decision-intro-texts",
  decisionId,
  texts: [
    {
      documentType,
      language,
      translationOption,
      text: text ?? "",
    },
  ],
});

export type FetchDecisionStatusEvent = {
  type: "dbot/fetch-decision-status";
};

export const fetchDecisionStatusEvent = (): FetchDecisionStatusEvent => ({
  type: "dbot/fetch-decision-status",
});

export type FetchDecisionStatusResultEvent = {
  type: "dbot/fetch-decision-status-result";
  data: DecisionStatusResponse;
};

export const fetchDecisionStatusResultEvent = (
  data: DecisionStatusResponse,
): FetchDecisionStatusResultEvent => ({
  type: "dbot/fetch-decision-status-result",
  data,
});

export type UpdateFollowUpStatusEvent = {
  type: "dbot/update-follow-up-status";
  orgNo: string;
  decisionId: string;
  followUpData: FollowUpData;
};

export const updateFollowUpStatusEvent = (
  orgNo: string,
  decisionId: string,
  followUpData: FollowUpData,
): UpdateFollowUpStatusEvent => ({
  type: "dbot/update-follow-up-status",
  orgNo,
  decisionId,
  followUpData,
});

export type UpdateFollowUpStatusResultEvent = {
  type: "dbot/update-follow-up-status-result";
  data: DecisionStatusResponse;
};

export const updateFollowUpStatusResultEvent = (
  data: DecisionStatusResponse,
): UpdateFollowUpStatusResultEvent => ({
  type: "dbot/update-follow-up-status-result",
  data,
});

export type ApplyDecisionEvent = {
  type: "dbot/apply-decision";
  orgNo: string;
  decisionId: string;
  decisionType: DecisionType;
  subscription?: IncorporationActivationSubscription;
};
export const applyDecisionEvent = (
  orgNo: string,
  decisionId: string,
  decisionType: DecisionType,
  subscription?: IncorporationActivationSubscription,
): ApplyDecisionEvent => ({
  type: "dbot/apply-decision",
  orgNo,
  decisionId,
  decisionType,
  subscription,
});

export type ApplyDecisionResultEvent = {
  type: "dbot/apply-decision-result";
  orgNo: string;
  decisionId: string;
  result: Ok | Failure<TMessage>;
};
export const applyDecisionSuccessEvent = ({
  orgNo,
  decisionId,
}: {
  orgNo: string;
  decisionId: string;
}): ApplyDecisionResultEvent => ({
  type: "dbot/apply-decision-result",
  orgNo,
  decisionId,
  result: ok(undefined),
});
export const applyDecisionFailureEvent = (
  {
    orgNo,
    decisionId,
  }: {
    orgNo: string;
    decisionId: string;
  },
  message: TMessage,
): ApplyDecisionResultEvent => ({
  type: "dbot/apply-decision-result",
  orgNo,
  decisionId,
  result: failure(message),
});

export type SendToBRDecision = { decisionId: string; isRegistration: boolean };

export type SendToBREvent = {
  type: "dbot/send-to-br";
  orgNo: string;
  decisionId: string;
  decisions: SendToBRDecision[];
  signers: Record<string, BRSigner[]>;
};

export const sendToBREvent = (
  orgNo: string,
  decisionId: string,
  decisions: SendToBRDecision[],
  signers: Record<string, BRSigner[]>,
): SendToBREvent => ({
  type: "dbot/send-to-br",
  orgNo,
  decisionId,
  decisions,
  signers,
});

export type SendIncorporationToBREvent = {
  type: "dbot/send-incorporation-to-br";
  orgNo: string;
  decisionId: string;
};

export const sendIncorporationToBREvent = (
  orgNo: string,
  decisionId: string,
): SendIncorporationToBREvent => ({
  type: "dbot/send-incorporation-to-br",
  orgNo,
  decisionId,
});

export type CheckIncorporationStatusEvent = {
  type: "dbot/check-incorporation-status";
  decisionId: string;
};

export const checkIncorporationStatusEvent = (
  decisionId: string,
): CheckIncorporationStatusEvent => ({
  type: "dbot/check-incorporation-status",
  decisionId,
});

export type CheckIncorporationStatusResultEvent = {
  type: "dbot/check-incorporation-status-result";
  decisionId: string;
  result: Ok<DseStatusResponse> | Failure<TMessage>;
};

export const checkIncorporationStatusSuccessEvent = (
  decisionId: string,
  data: DseStatusResponse,
): CheckIncorporationStatusResultEvent => ({
  type: "dbot/check-incorporation-status-result",
  decisionId,
  result: ok(data),
});

export const checkIncorporationStatusFailureEvent = (
  decisionId: string,
  message: TMessage,
): CheckIncorporationStatusResultEvent => ({
  type: "dbot/check-incorporation-status-result",
  decisionId,
  result: failure(message),
});

export type DseSimulatedBrResponseKind = "AVVIS" | "VEDTAK" | "NEKTET" | "FAKTURA";

export type SimulateBrResponseEvent = {
  type: "dbot/simulate-br-response";
  decisionId: string;
  kind: DseSimulatedBrResponseKind;
};

export const simulateBrResponseEvent = (
  decisionId: string,
  kind: DseSimulatedBrResponseKind,
): SimulateBrResponseEvent => ({
  type: "dbot/simulate-br-response",
  decisionId,
  kind,
});

export type IncorporationActivationInfo = {
  admins: { id: string; name: string }[];
  enterpriseAccounts: { id: string; name: string }[];
};

export type FetchIncorporationActivationInfoEvent = {
  type: "dbot/fetch-incorporation-activation-info";
};

export const fetchIncorporationActivationInfoEvent = (): FetchIncorporationActivationInfoEvent => ({
  type: "dbot/fetch-incorporation-activation-info",
});

export type FetchIncorporationActivationInfoResultEvent = {
  type: "dbot/fetch-incorporation-activation-info-result";
  result: Ok<IncorporationActivationInfo> | Failure<TMessage>;
};

export const fetchIncorporationActivationInfoSuccessEvent = (
  data: IncorporationActivationInfo,
): FetchIncorporationActivationInfoResultEvent => ({
  type: "dbot/fetch-incorporation-activation-info-result",
  result: ok(data),
});

export const fetchIncorporationActivationInfoFailureEvent = (
  message: TMessage,
): FetchIncorporationActivationInfoResultEvent => ({
  type: "dbot/fetch-incorporation-activation-info-result",
  result: failure(message),
});

export type CreateMergerCounterpartEvent = {
  type: "dbot/create-merger-counterpart";
  decisionId: string;
};

export const createMergerCounterpartEvent = (decisionId: string): CreateMergerCounterpartEvent => ({
  type: "dbot/create-merger-counterpart",
  decisionId,
});

export type CreateMergerCounterpartResultEvent = {
  type: "dbot/create-merger-counterpart-result";
  result: Ok<void> | Failure<TMessage>;
};

export const createMergerCounterpartSuccessEvent = (): CreateMergerCounterpartResultEvent => ({
  type: "dbot/create-merger-counterpart-result",
  result: ok(undefined),
});

export const createMergerCounterpartFailureEvent = (
  message: TMessage,
): CreateMergerCounterpartResultEvent => ({
  type: "dbot/create-merger-counterpart-result",
  result: failure(message),
});

export type IncorporationActivationSubscription = {
  enterpriseAccountId?: string;
  productName: ProductName;
};

export const defaultIncorporationActivationSubscription: IncorporationActivationSubscription = {
  productName: "digital_company",
};

export type SelectIncorporationActivationSubscriptionEvent = {
  type: "dbot/select-incorporation-activation-subscription";
  decisionId: string;
  subscription: IncorporationActivationSubscription;
};

export const selectIncorporationActivationSubscriptionEvent = (
  decisionId: string,
  subscription: IncorporationActivationSubscription,
): SelectIncorporationActivationSubscriptionEvent => ({
  type: "dbot/select-incorporation-activation-subscription",
  decisionId,
  subscription,
});

export type PrekontrollIncorporationEvent = {
  type: "dbot/prekontroll-incorporation";
  orgNo: string;
  decisionId: string;
};

export const prekontrollIncorporationEvent = (
  orgNo: string,
  decisionId: string,
): PrekontrollIncorporationEvent => ({
  type: "dbot/prekontroll-incorporation",
  orgNo,
  decisionId,
});

export type DsePrekontrollErrorItem = {
  feilid?: number;
  section?: string;
  description?: string;
  dynamicReturn?: string;
  localCode?: string;
  params?: Record<string, string | number | undefined>;
};

const founderErrorParams = (error: DsePrekontrollErrorItem) => ({
  name: error.params?.name,
  orgNo: error.params?.orgNo,
  zip: error.params?.zip,
});

const dsePrekontrollLocalErrorMessages: Record<
  string,
  (error: DsePrekontrollErrorItem) => TMessage
> = {
  BUSINESS_ADDRESS_POSTBOX_ZIP: (error) => ({
    key: "dbot.summary.sendToBR.incorporation.error.business-address-postbox-zip",
    params: { zip: error.params?.zip ?? error.dynamicReturn },
  }),
  BUSINESS_ADDRESS_NOT_IN_MATRIKKEL: (error) => ({
    key: "dbot.summary.sendToBR.incorporation.error.business-address-not-in-matrikkel",
    params: { address: error.params?.address, zip: error.params?.zip },
  }),
  BUSINESS_ADDRESS_AMBIGUOUS_IN_MATRIKKEL: (error) => ({
    key: "dbot.summary.sendToBR.incorporation.error.business-address-ambiguous-in-matrikkel",
    params: { address: error.params?.address, zip: error.params?.zip },
  }),
  FOUNDER_MISSING_ADDRESS: (error) => ({
    key: "dbot.summary.sendToBR.incorporation.error.founder-missing-address",
    params: founderErrorParams(error),
  }),
  FOUNDER_INVALID_ZIP: (error) => ({
    key: "dbot.summary.sendToBR.incorporation.error.founder-invalid-zip",
    params: founderErrorParams(error),
  }),
  FOUNDER_POSTBOX_ZIP: (error) => ({
    key: "dbot.summary.sendToBR.incorporation.error.founder-postbox-zip",
    params: founderErrorParams(error),
  }),
  FOUNDER_MISSING_FULLMEKTIG: (error) => ({
    key: "dbot.summary.sendToBR.incorporation.error.founder-missing-fullmektig",
    params: founderErrorParams(error),
  }),
  SIGNER_MISSING_FNR: (error) => ({
    key: "dbot.summary.sendToBR.incorporation.error.signer-missing-fnr",
    params: { name: error.params?.name },
  }),
  UNSUPPORTED_CHARACTERS: (error) => ({
    key: "dbot.summary.sendToBR.incorporation.error.unsupported-characters",
    params: { text: error.params?.text, characters: error.params?.characters },
  }),
};

export const dsePrekontrollErrorMessage = (error: DsePrekontrollErrorItem): TMessage => {
  const localMessage = error.localCode
    ? dsePrekontrollLocalErrorMessages[error.localCode]
    : undefined;
  if (localMessage) {
    return localMessage(error);
  }
  if (error.description) {
    return { translated: error.description };
  }
  return { key: "dbot.summary.sendToBR.incorporation.error.validation" };
};

export type PrekontrollIncorporationResultEvent = {
  type: "dbot/prekontroll-incorporation-result";
  orgNo: string;
  decisionId: string;
  result: Ok<undefined> | Failure<TMessage>;
  errors?: DsePrekontrollErrorItem[];
  code?: string;
};

export const prekontrollIncorporationSuccessEvent = (
  orgNo: string,
  decisionId: string,
): PrekontrollIncorporationResultEvent => ({
  type: "dbot/prekontroll-incorporation-result",
  orgNo,
  decisionId,
  result: ok(undefined),
});

export const prekontrollIncorporationFailureEvent = (
  orgNo: string,
  decisionId: string,
  message: TMessage,
  errors?: DsePrekontrollErrorItem[],
  code?: string,
): PrekontrollIncorporationResultEvent => ({
  type: "dbot/prekontroll-incorporation-result",
  orgNo,
  decisionId,
  result: failure(message),
  errors,
  code,
});

export type DseSubmitErrorCode =
  | "MISSING_INCORPORATION_DOCUMENT"
  | "DOCUMENT_NOT_FULLY_SIGNED"
  | "VALIDATION_ERROR"
  | "BRREG_UNAVAILABLE"
  | "UNKNOWN_ERROR";

const dseSubmitErrorMessageKey: Record<DseSubmitErrorCode, TMessageKey> = {
  MISSING_INCORPORATION_DOCUMENT: "dbot.summary.sendToBR.incorporation.error.missing-document",
  DOCUMENT_NOT_FULLY_SIGNED: "dbot.summary.sendToBR.incorporation.error.document-not-fully-signed",
  VALIDATION_ERROR: "dbot.summary.sendToBR.incorporation.error.validation",
  BRREG_UNAVAILABLE: "dbot.summary.sendToBR.incorporation.error.brreg-unavailable",
  UNKNOWN_ERROR: "dbot.summary.sendToBR.incorporation.error.unknown",
};

export const dseSubmitErrorMessage = (code: string | undefined): TMessage => {
  const key =
    (code !== undefined && (dseSubmitErrorMessageKey as Record<string, TMessageKey>)[code]) ||
    dseSubmitErrorMessageKey.UNKNOWN_ERROR;
  return { key };
};

export const isDseSystemUnavailable = (code: string | undefined): boolean =>
  code === "BRREG_UNAVAILABLE" || code === "UNKNOWN_ERROR";

export const openIncorporationDecisionAction = "#open-incorporation-decision";

const prekontrollValidationErrors: TMessage = {
  key: "dbot.summary.prekontroll.incorporation.error.validation.errors",
};

const prekontrollFailureMessage = (
  code: string | undefined,
  errors: TMessage | TMessageParam,
): TMessage => {
  switch (code) {
    case "VALIDATION_ERROR":
      return {
        key: "dbot.summary.prekontroll.incorporation.error.validation",
        params: { errors },
      };
    case "BRREG_UNAVAILABLE":
      return { key: "dbot.summary.prekontroll.incorporation.error.brreg-unavailable" };
    default:
      return { key: "dbot.summary.prekontroll.incorporation.error.unknown" };
  }
};

export const dsePrekontrollFailureMessage = (code: string | undefined): TMessage =>
  prekontrollFailureMessage(code, prekontrollValidationErrors);

export const dsePrekontrollFailureMessageWithDecisionLink = (code: string | undefined): TMessage =>
  prekontrollFailureMessage(code, {
    type: "text",
    value: `[{{errors}}](${openIncorporationDecisionAction})`,
    params: { errors: prekontrollValidationErrors },
  });

export type SendToBRResultEvent = {
  type: "dbot/send-to-br-result";
  orgNo: string;
  decisionId: string;
  result: Ok<TMessage> | Failure<TMessage>;
};

export const sendToBRSuccessEvent = ({
  orgNo,
  decisionId,
}: {
  orgNo: string;
  decisionId: string;
}): SendToBRResultEvent => ({
  type: "dbot/send-to-br-result",
  orgNo,
  decisionId,
  result: ok({ key: "dbot.summary.sendToBR.success" }),
});

export const sendToBRFailureEvent = (
  { orgNo, decisionId }: { orgNo: string; decisionId: string },
  message: TMessage,
): SendToBRResultEvent => ({
  type: "dbot/send-to-br-result",
  orgNo,
  decisionId,
  result: failure(message),
});

export type SkipSendToBREvent = {
  type: "dbot/skip-send-to-br";
  orgNo: string;
  decisionId: string;
};

export const skipSendToBREvent = (orgNo: string, decisionId: string): SkipSendToBREvent => ({
  type: "dbot/skip-send-to-br",
  orgNo,
  decisionId,
});

export type SkipSendToBRResultEvent = {
  type: "dbot/skip-send-to-br-result";
  orgNo: string;
  decisionId: string;
};

export const skipSendToBRResultEvent = (
  orgNo: string,
  decisionId: string,
): SkipSendToBRResultEvent => ({
  type: "dbot/skip-send-to-br-result",
  orgNo,
  decisionId,
});

export type DecisionAddedEvent = {
  type: "dbot/decision-added";
  decisionId: string;
  isFreetext: boolean;
};

export type DecisionRemovedEvent = {
  type: "dbot/decision-removed";
  decisionId: string;
};

export type PowerOfAttorneyForGeneralMeeting = {
  shareholderId: number;
  poaRecipientName: string;
  poaRecipientEmail: string;
  poaRecipientRole: PoARecipientRole | undefined;
  poaRecipientCompanyRoleId?: number;
  poaRecipientShareholderId?: number;
};

export type SaveParticipationEvent = {
  type: "dbot/save-participation";
  createParticipation: GeneralMeetingParticipation[];
  updateParticipation: GeneralMeetingParticipation[];
};

export type GeneralMeetingParticipation = {
  participationId?: string;
  shareholderId: number;
  isPresent: boolean;
  representedByName?: string;
  representedById?: number;
  registeredBy?: number;
  registeredAt?: string;
};

export type UploadAttachmentsEvent = {
  type: "dbot/upload-attachments";
  files: File[];
  reference: string;
  attachmentType: AttachmentType;
  selectedDocuments?: DocumentType[];
  source: unknown;
  overrideExistingAttachmentType?: boolean;
  onSuccess?: DbotEvent[];
  onUploaded?: (attachments: UploadedAttachment[]) => void;
};

export const uploadAttachmentsEvent = (params: {
  files: File[];
  reference: string;
  attachmentType: AttachmentType;
  source: unknown;
  overrideExistingAttachmentType?: boolean;
  selectedDocuments?: DocumentType[];
  onSuccess?: DbotEvent[];
  onUploaded?: (attachments: UploadedAttachment[]) => void;
}): UploadAttachmentsEvent => ({ type: "dbot/upload-attachments", ...params });

export type DeleteAttachmentEvent = {
  type: "dbot/delete-attachment";
  attachmentId: string;
  source: unknown;
  reference?: string;
  onSuccess?: DbotEvent[];
};

export const deleteAttachmentEvent = (params: {
  attachmentId: string;
  source: unknown;
  reference?: string;
  onSuccess?: DbotEvent[];
}): DeleteAttachmentEvent => ({
  type: "dbot/delete-attachment",
  ...params,
});

export type KeepOriginalFormatEvent = {
  type: "dbot/keep-original-format";
  attachmentId: string;
};

export type DeleteAttachmentResultEvent = {
  type: "dbot/delete-attachment-result";
  attachmentId: string;
  source?: unknown;
  reference?: string;
};

export type DeleteDocumentBlobEvent = {
  type: "dbot/delete-document-blob";
  projectDocumentId: string;
  orgNo: string;
  onComplete?: (DbotEvent | DCEvent)[];
};

export type OpenDocumentBlobEvent = {
  type: "dbot/open-document-blob";
  orgNo: string;
};

export type UploadDocumentBlobEvent = {
  type: "dbot/upload-document-blob";
  projectDocumentId: string;
  orgNo: string;
  files: File[];
};

export const deleteAttachmentSuccessEvent = (
  attachmentId: string,
  source?: unknown,
  reference?: string,
): DeleteAttachmentResultEvent => ({
  type: "dbot/delete-attachment-result",
  attachmentId,
  source,
  reference,
});

export type DownloadAttachmentEvent = {
  type: "dbot/download-attachment";
  attachmentId: string;
};

export type OpenAttachmentEvent = {
  type: "dbot/open-attachment";
  attachmentId: string;
  filename?: string;
};

export type OpenAttachmentPreviewEvent = {
  type: "dbot/open-attachment-preview";
  attachmentId: string;
  filename?: string;
};

export type SaveGeneralMeetingVotesEvent = {
  type: "dbot/save-general-meeting-votes";
  voteType: "meetChair" | "approveNoticeAndAgenda" | "decision";
  decisionId: string | null;
  votes: GeneralMeetingIndividualVote[];
};

export type SaveGeneralMeetingVotesResultEvent = {
  type: "dbot/save-general-meeting-votes-result";
  result: "ok" | "error";
};

export const saveGeneralMeetingVotesSuccessEvent = (): SaveGeneralMeetingVotesResultEvent => ({
  type: "dbot/save-general-meeting-votes-result",
  result: "ok",
});

export const saveGeneralMeetingVotesErrorEvent = (): SaveGeneralMeetingVotesResultEvent => ({
  type: "dbot/save-general-meeting-votes-result",
  result: "error",
});

export type UpdateGeneralMeetingEvent = {
  type: "dbot/update-general-meeting";
  meeting: ElectronicGeneralMeeting;
};

export type FetchGeneralMeetingVotesEvent = {
  type: "dbot/fetch-general-meeting-votes";
};

export type FetchGeneralMeetingVotesResultEvent = {
  type: "dbot/fetch-general-meeting-votes-result";
  votes: GeneralMeetingVotesResponse;
};

export const fetchVotingResultEvent = (
  votes: GeneralMeetingVotesResponse,
): FetchGeneralMeetingVotesResultEvent => ({
  type: "dbot/fetch-general-meeting-votes-result",
  votes,
});

export type SearchCompaniesEvent = {
  type: "dbot/search-companies";
  input: string;
  source: unknown;
};

export type SearchCompaniesResultEvent = {
  type: "dbot/search-companies-result";
  result: SuggestedCompany[];
  source: unknown;
};

export const searchCompaniesResultEvent = (
  source: unknown,
  data?: SuggestedCompany[],
): SearchCompaniesResultEvent => ({
  type: "dbot/search-companies-result",
  result: data ?? [],
  source,
});

export type NaceSearchResult = {
  code: string;
  aktivitetBransjeKode?: string;
  label: string;
  description: string;
  category: string;
};

export const NACE_SEARCH_MIN_QUERY_LENGTH = 2;

export type SearchNaceEvent = {
  type: "dbot/search-nace";
  input: string;
  source: unknown;
};

export type SearchNaceResultEvent = {
  type: "dbot/search-nace-result";
  result: NaceSearchResult[];
  source: unknown;
};

export const searchNaceResultEvent = (
  source: unknown,
  data?: NaceSearchResult[],
): SearchNaceResultEvent => ({
  type: "dbot/search-nace-result",
  result: data ?? [],
  source,
});

export type SearchMunicipalitiesEvent = {
  type: "dbot/search-municipalities";
  input: string;
  source: unknown;
};

export type SearchMunicipalitiesResultEvent = {
  type: "dbot/search-municipalities-result";
  result: Municipality[];
  source: unknown;
};

export const searchMunicipalitiesResultEvent = (
  source: unknown,
  data?: Municipality[],
): SearchMunicipalitiesResultEvent => ({
  type: "dbot/search-municipalities-result",
  result: data ?? [],
  source,
});

export type MatrikkelObjectType = "VEGADRESSE" | "MATRIKKELADRESSE";

export type MatrikkelAddress = {
  addressText: string;
  postalCode: string;
  city: string;
  objectType: MatrikkelObjectType;
  municipalityNumber?: string;
  municipality?: string;
};

export type MatrikkelVerificationStatus = "VERIFIED" | "NOT_FOUND" | "AMBIGUOUS" | "UNAVAILABLE";

export type MatrikkelAddressVerification = {
  status: MatrikkelVerificationStatus;
  address?: MatrikkelAddress;
  hits?: number;
};

export type VerifyMatrikkelAddressEvent = {
  type: "dbot/verify-matrikkel-address";
  address: string;
  postalCode: string;
  source: unknown;
};

export type VerifyMatrikkelAddressResultEvent = {
  type: "dbot/verify-matrikkel-address-result";
  result: MatrikkelAddressVerification;
  source: unknown;
};

export const verifyMatrikkelAddressResultEvent = (
  source: unknown,
  data?: MatrikkelAddressVerification,
): VerifyMatrikkelAddressResultEvent => ({
  type: "dbot/verify-matrikkel-address-result",
  result: data ?? { status: "UNAVAILABLE" },
  source,
});

export type ProviderRole = "auditor" | "accountant";

export type ProviderValidity = "VALID" | "INVALID" | "UNAVAILABLE";

export type CheckProviderValidityEvent = {
  type: "dbot/check-provider-validity";
  role: ProviderRole;
  organisationNumber: string;
  source: unknown;
};

export type CheckProviderValidityResultEvent = {
  type: "dbot/check-provider-validity-result";
  result: ProviderValidity;
  source: unknown;
};

export const checkProviderValidityResultEvent = (
  source: unknown,
  data?: ProviderValidity,
): CheckProviderValidityResultEvent => ({
  type: "dbot/check-provider-validity-result",
  result: data ?? "UNAVAILABLE",
  source,
});

export type CompanyNameProblem =
  | "INVALID_CHARACTERS"
  | "TOO_FEW_LETTERS"
  | "GEOGRAPHIC_NAME"
  | "IDENTICAL_NAME"
  | "COMPANY_TYPE_SUFFIX"
  | "UNSPECIFIED";

export type CompanyNameVerdict = "ACCEPTED" | "REJECTED" | "UNAVAILABLE";

export type CompanyNameCheck = {
  verdict: CompanyNameVerdict;
  problems: CompanyNameProblem[];
};

export type CheckCompanyNameEvent = {
  type: "dbot/check-company-name";
  name: string;
  source: unknown;
};

export type CheckCompanyNameResultEvent = {
  type: "dbot/check-company-name-result";
  result: CompanyNameCheck;
  source: unknown;
};

export const checkCompanyNameResultEvent = (
  source: unknown,
  data?: CompanyNameCheck,
): CheckCompanyNameResultEvent => ({
  type: "dbot/check-company-name-result",
  result: data ?? { verdict: "UNAVAILABLE", problems: [] },
  source,
});

export type SearchMatrikkelAddressEvent = {
  type: "dbot/search-matrikkel-address";
  input: string;
  source: unknown;
};

export type SearchMatrikkelAddressResultEvent = {
  type: "dbot/search-matrikkel-address-result";
  result: MatrikkelAddress[];
  source: unknown;
};

export const searchMatrikkelAddressResultEvent = (
  source: unknown,
  data?: MatrikkelAddress[],
): SearchMatrikkelAddressResultEvent => ({
  type: "dbot/search-matrikkel-address-result",
  result: data ?? [],
  source,
});

export type SendForReviewEvent = {
  type: "dbot/send-for-review";
  documentId: string;
  options?: OptionsBeforeSending;
  sendMessageRequest: CreateAndSendMessageRequest;
};

export type SendForReviewResultEvent = {
  type: "dbot/send-for-review-result";
  result: Ok | Failure;
};

export type NavigateToProjectListEvent = {
  type: "dbot/navigate-to-project-list";
};

export type EditProjectNameEvent = {
  type: "dbot/edit-project-name";
};

export type SaveProjectSuccessEvent = {
  type: "dbot/save-project-success";
};

export type DecisionsSavedEvent = {
  type: "dbot/decisions-saved";
  saved: Decision[];
};

export type SaveProjectScheduledEvent = {
  type: "dbot/save-project-scheduled";
};

export type UpdateVotingInGeneralMeetingEvent = {
  type: "dbot/update-voting-in-general-meeting";
  voting: GeneralMeetingVoting;
  voteType: GeneralMeetingVoteType;
  decisionId?: string;
};

export type UpdateAllVotesInGeneralMeetingEvent = {
  type: "dbot/update-all-votes-in-general-meeting";
  votes: GeneralMeetingVotes;
};

export type UpdateVotingTypeInGeneralMeetingEvent = {
  type: "dbot/update-voting-type-in-general-meeting";
  voteType: GeneralMeetingVoteType;
  votingType?: GeneralMeetingVotingType;
  decisionId?: string;
};

export type SaveProjectNameEvent = {
  type: "dbot/save-project-name";
  name: string;
};

export type OpenMobileDocumentPreviewEvent = {
  type: "dbot/open-mobile-document-preview";
};

export type CloseMobileDocumentPreviewEvent = {
  type: "dbot/close-mobile-document-preview";
};

export type DuplicateProjectEvent = {
  type: "dbot/duplicate-project";
  name: string;
};

export type DeleteProjectEvent = {
  type: "dbot/delete-project";
  alert?: AlertMessage;
  onSuccess?: (DbotEvent | DCEvent)[];
};

export type ConfirmDeleteProjectEvent = {
  type: "dbot/confirm-delete-project";
};

export type CancelDeleteProjectEvent = {
  type: "dbot/cancel-delete-project";
};

export type ToggleFullscreenEvent = {
  type: "dbot/toggle-fullscreen";
};

export type ToggleMeetingScreenShareModeEvent = {
  type: "dbot/toggle-screen-share-mode";
};

export type DbotAction = {
  icon?: IconName;
  label: TMessage;
  event: DbotEvent;
};

export type ElectronicGeneralMeetingStep =
  | { type: "registration" }
  | { type: "choiceOfMeetingChair" }
  | { type: "approveAgenda" }
  | { type: "decision"; decisionId: string };

export type DetailsPanel = {
  type: "decision" | "company" | "custom" | "generalmeeting" | "follow-up";
  id: string;
  documentId?: string;
  followUp?: {
    item: FollowUp;
    orgNo: string;
    decisionId: string;
    attachmentType?: AttachmentType;
    isBRRegistration?: boolean;
  };
  icon: IconName | IconProps;
  heading?: TMessage;
  summaryHeading?: TMessage;
  label?: TMessage | TMessageParam;
  hasValidationError?: boolean;
  tag?: TMessage;
  action?: DbotAction;
  customOpenEvent?: DbotEvent | DCEvent;
  isDisabled?: boolean;
  notReady?: boolean;
  infoMessage?: TMessage;
  generalMeetingStep?: ElectronicGeneralMeetingStep;
  canRemove?: boolean;
};

export type DecisionDetailsPanel = {
  type: "decision";
  decisionId: string;
  documentId?: string;
  orgNo?: string;
};

export type CustomDetailsPanel = {
  type: "custom";
  id: string;
  documentId?: string;
};

export type GeneralMeetingDetailsPanel = {
  type: "generalmeeting";
  id: string;
  step: ElectronicGeneralMeetingStep;
};

export type CompanyDetailsPanel = {
  type: "company";
  orgNo: string;
};

export type FollowUpDetailsPanel = {
  type: "follow-up";
  followUpType: FollowUpType;
  orgNo: string;
  decisionId: string;
  attachmentType?: AttachmentType;
  isBRRegistration?: boolean;
};

export type DetailsPanelRef =
  | CompanyDetailsPanel
  | CustomDetailsPanel
  | DecisionDetailsPanel
  | FollowUpDetailsPanel
  | GeneralMeetingDetailsPanel;

export type ShowGeneralMeetingDialogEvent = {
  type: "dbot/show-general-meeting-dialog";
  voteType?: GeneralMeetingVoteType;
  decisionId?: string;
};

export type UpdateDocumentPreview = {
  type: "dbot/update-document-preview";
  updatedPreviews: UpdatedPreview[];
};

export type OpenDetailsPanelEvent = {
  type: "dbot/open-details-panel";
  panel: DetailsPanelRef;
};

export type CloseDetailsPanelEvent = {
  type: "dbot/close-details-panel";
};

export type ToggleMaximizeDetailsPanelEvent = {
  type: "dbot/toggle-maximize-details-panel";
};

export type UpdateElectronicGeneralMeetingStatusEvent = {
  type: "dbot/electronic-general-meeting-update-status";
  projectId: string;
  statusName: StatusName;
  status: GeneralMeetingStatus;
  decisionId?: string;
};

export type UpdateElectronicGeneralMeetingStatusResultEvent = {
  type: "dbot/electronic-general-meeting-update-status-result";
  result: Result<ElectronicGeneralMeeting>;
};

export type TabId = "companies" | "documents" | "follow-up" | "matters" | "meetings";

export type OpenTabEvent = {
  type: "dbot/open-tab";
  tab: TabId;
};

export type ToggleDocumentPanelEvent = {
  type: "dbot/toggle-document-panel";
  documentId: string;
  expanded: boolean;
  previewBaseVersion?: boolean;
};

export type ConfirmRemoveDocumentEvent = {
  type: "dbot/confirm-remove-document";
  documentId: string;
  onSuccess: DbotEvent[];
};

///
/// Decision actions
///

export type OpenIncreaseOfShareCapitalSubscribersStatusEvent = {
  type: "dbot/open-increase-of-share-capital-subscribers-status";
  orgNo: string;
  decisionId: string;
  isLocked: boolean;
};

export type OpenIncreaseOfShareCapitalSubscribersAllocationEvent = {
  type: "dbot/open-increase-of-share-capital-subscribers-allocation";
  orgNo: string;
  decisionId: string;
  isSentToBR: boolean;
};

export type ApplyDecisionAction = {
  type: "dbot/applyDecisionAction";
  orgNo: string;
  decision: DecisionItem;
};

export const applyDecisionAction = (orgNo: string, decision: DecisionItem): ApplyDecisionAction => {
  return {
    type: "dbot/applyDecisionAction",
    orgNo,
    decision,
  };
};

export type ConfirmSendToBRAction = {
  type: "dbot/confirm-send-to-br";
  orgNo: string;
  decisionId: string;
  isRegistration: boolean;
};

export const confirmSendToBRAction = (
  orgNo: string,
  decisionId: string,
  isRegistration: boolean,
): ConfirmSendToBRAction => {
  return {
    type: "dbot/confirm-send-to-br",
    orgNo,
    decisionId,
    isRegistration,
  };
};

export type ConfirmSendIncorporationToBRAction = {
  type: "dbot/confirm-send-incorporation-to-br";
  orgNo: string;
  decisionId: string;
};

export const confirmSendIncorporationToBRAction = (
  orgNo: string,
  decisionId: string,
): ConfirmSendIncorporationToBRAction => ({
  type: "dbot/confirm-send-incorporation-to-br",
  orgNo,
  decisionId,
});

export type ConfirmSkipSendToBRAction = {
  type: "dbot/confirm-skip-send-to-br";
  orgNo: string;
  decisionId: string;
};

export const confirmSkipSendToBRAction = (
  orgNo: string,
  decisionId: string,
): ConfirmSkipSendToBRAction => {
  return {
    type: "dbot/confirm-skip-send-to-br",
    orgNo,
    decisionId,
  };
};

///
/// Document actions
///

export type OpenSubscriptionStatusEvent = {
  type: "dbot/open-subscription-status";
  document: DocumentItem;
  label: TMessage;
};

export type DownloadDocumentAction = {
  type: "dbot/download-document";
  document: DocumentItem;
  label: TMessage;
};

export type DownloadSubscriptionFormsAction = {
  type: "dbot/download-subscription-forms";
  document: DocumentItem;
  label: TMessage;
};

export type OpenSubscriptionFormBatchPreviewDialogAction = {
  type: "dbot/openSubscriptionFormBatchPreviewDialog";
  label: TMessage;
  title: TMessage;
  filePreview: (file: FileToPreview) => FilePreviewProps;
};

export type OpenReviewDialogAction = {
  type: "dbot/open-review-dialog";
  document: DocumentItem;
  label: TMessage;
  showUnsupportedAttachmentWarning?: boolean;
  isResend?: boolean;
  signers?: Signer[];
  documents?: ProjectDocumentPreview[];
  companies?: CompanyItem[];
  emailBody?: TMessage;
  lockedSigningMethod?: SigningMethod;
  destinationFolders?: DestinationFolder[];
  preselectedDestinationFolder?: DestinationFolder;
  authMethod?: MeetingAuthMethod;
  meetingPassword?: string;
  isSendToNewParticipants?: boolean;
  attachments?: UploadedAttachment[];
};

export type BundledDocument = {
  documentId: string;
  documentType: DocumentType;
  title: string;
  signers: Signer[];
  previews: ProjectDocumentPreview[];
  hasUnsupportedAttachments: boolean;
  attachments: UploadedAttachment[];
  isCurrent: boolean;
};

export type OpenBundleSelectionDialogAction = {
  type: "dbot/open-bundle-selection-dialog";
  currentDocument: DocumentItem;
  documents: BundledDocument[];
  companies: CompanyItem[];
};

export type OpenPackageSigningDialogAction = {
  type: "dbot/open-package-signing-dialog";
  documents: BundledDocument[];
  companies: CompanyItem[];
  emailBody: TMessage;
  destinationFolders: DestinationFolder[];
};

export type ProjectValidationFailedEvent = {
  type: "dbot/project-validation-failed";
  validationError: TMessage;
};

export type ValidateProjectAction = {
  type: "dbot/validate-project-action";
  contextDocumentId?: string;
  successAction: DbotEvent;
  label: TMessage;
};

export type OpenSigningStatusDialogAction = {
  type: "dbot/open-signing-status-dialog";
  document: DocumentItem;
  label: TMessage;
};

export type OpenChangePermissionDialogAction = {
  type: "dbot/open-change-permission-dialog";
};

export type OpenViewingStatusDialogAction = {
  type: "dbot/open-viewing-status-dialog";
  document: DocumentItem;
  label: TMessage;
};

export type OpenSaveAsTemplateDialog = {
  type: "dbot/open-save-as-template-dialog";
};

export type RefreshSigningStatusesAction = {
  type: "dbot/refresh-signing-status";
  document: DocumentItem;
  label: TMessage;
};

export type UnlockDocumentAction = {
  type: "dbot/unlock-document";
  documentId: string;
  label: TMessage;
};

export type UpdateMinutesLiveParticipationEvent = {
  type: "dbot/update-minutes-live-participation";
  participationList: GeneralMeetingParticipation[];
};

export const updateMinutesLiveParticipationEvent = (
  participationList: GeneralMeetingParticipation[],
): UpdateMinutesLiveParticipationEvent => {
  return {
    type: "dbot/update-minutes-live-participation",
    participationList,
  };
};

export type SendForReviewMessageTemplate = {
  subject: TMessage;
  body: TMessage;
  linkText: TMessage;
  senderName: string;
};

export type OpenSendForReviewDialogAction = {
  type: "dbot/open-send-for-review-dialog";
  document: DocumentItem;
  label: TMessage;
  messageTemplate: SendForReviewMessageTemplate;
  recipients?: CompanyRoleIdWithName[];
};

export type MoveDocumentAttachmentEvent = {
  type: "dbot/move-document-attachment";
  documentId: string;
  attachmentId: string;
  toAttachmentId: string;
};

export type MoveDecisionAttachmentEvent = {
  type: "dbot/move-decision-attachment";
  decisionId: string;
  attachmentId: string;
  toAttachmentId: string;
};

export type MoveDecisionEvent = {
  type: "dbot/move-decision";
  decisionId: string;
  toDecisionId: string;
};

export type SaveSelectedBatchCompaniesEvent = {
  type: "dbot/save-selected-batch-companies";
  companies: string[];
  onSuccess: (DbotEvent | DCEvent)[];
  onFailure: (DbotEvent | DCEvent)[];
};

export type FetchDocumentSignersEvent = {
  type: "dbot/fetch-document-signers";
  documentId: string;
  onSuccess: (data: GetProjectDocumentSignersResponse) => DbotEvent[];
  onFailure: (DbotEvent | DCEvent)[];
};

export type FetchDocumentRecipientsEvent = {
  type: "dbot/fetch-document-recipients";
  documentId: string;
  onSuccess: (data: GetProjectDocumentRecipientsResponse) => (DbotEvent | DCEvent)[];
  onFailure: (DbotEvent | DCEvent)[];
};

export type FetchDestinationFoldersEvent = {
  type: "dbot/fetch-destination-folders";
  orgNo?: string;
  onSuccess: (data: DestinationFoldersResponse) => (DbotEvent | DCEvent)[];
  onFailure: (DbotEvent | DCEvent)[];
};

export type FetchSignableDocumentsEvent = {
  type: "dbot/fetch-signable-documents";
  onSuccess: (data: GetSignableDocumentsResponse) => (DbotEvent | DCEvent)[];
  onFailure: (DbotEvent | DCEvent)[];
};

export type FetchProjectsForCopyEvent = {
  type: "dbot/fetch-projects-for-copy";
  projectId: string;
  onSuccess: (result: ProjectForCopy[]) => (DbotEvent | DCEvent)[];
  onFailure: (DbotEvent | DCEvent)[];
};

export type CloseDialogEvent = { type: "dbot/close-dialog" };

export type SelectCompanyEvent = { type: "dbot/select-company"; orgNo: string };

export type DbotEventDispatcher = (event: DbotEvent | DCEvent | (DbotEvent | DCEvent)[]) => void;

export type DbotEvent =
  | AddDecisionsEvent
  | AddDocumentsEvent
  | AddIncreaseOfShareCapitalSubscribersForIssueEvent
  | AddIncreaseOfShareCapitalSubscribersForNominalValueEvent
  | ApplyDecisionEvent
  | ApplyDecisionAction
  | ApplyDecisionResultEvent
  | AutosignDocumentEvent
  | AutoapproveSendToBREvent
  | CancelDeleteProjectEvent
  | ChangeStateEvent
  | CloseDetailsPanelEvent
  | CloseDialogEvent
  | CloseMobileDocumentPreviewEvent
  | ConfirmDeleteProjectEvent
  | ConfirmRemoveDocumentEvent
  | ConfirmSendToBRAction
  | ConfirmSendIncorporationToBRAction
  | ConfirmSkipSendToBRAction
  | CopyDecisionsEvent
  | DecisionAddedEvent
  | DecisionRemovedEvent
  | DecisionsSavedEvent
  | DeleteAttachmentEvent
  | DeleteAttachmentResultEvent
  | KeepOriginalFormatEvent
  | DeleteDocumentBlobEvent
  | DeleteProjectEvent
  | DocumentsAddedEvent
  | DownloadAttachmentEvent
  | DownloadDocumentAction
  | DownloadSubscriptionFormsAction
  | DownloadSignedDocumentEvent
  | DownloadSignedSubscriptionForms
  | DuplicateProjectEvent
  | EditProjectNameEvent
  | ExploreArticlesOfAssociationEvent
  | ExploreArticlesOfAssociationErrorEvent
  | ExploreArticlesOfAssociationSuccessEvent
  | FetchDecisionStatusEvent
  | FetchDecisionStatusResultEvent
  | FetchDestinationFoldersEvent
  | FetchDocumentSignersEvent
  | FetchSignableDocumentsEvent
  | FetchDocumentRecipientsEvent
  | FetchGeneralMeetingVotesEvent
  | FetchGeneralMeetingVotesResultEvent
  | FetchIncreaseOfShareCapitalSubscribersAllocationEvent
  | FetchIncreaseOfShareCapitalSubscribersAllocationResultEvent
  | FetchProjectsForCopyEvent
  | GetSigningStatusEvent
  | GetSigningStatusResultEvent
  | GetSubscriptionStatusEvent
  | GetSubscriptionStatusResultEvent
  | GetViewingStatusEvent
  | GetViewingStatusResultEvent
  | MoveDecisionEvent
  | MoveDecisionAttachmentEvent
  | MoveDocumentAttachmentEvent
  | NavigateToProjectListEvent
  | OpenAttachmentEvent
  | OpenAttachmentPreviewEvent
  | OpenSubscriptionFormBatchPreviewDialogAction
  | OpenDetailsPanelEvent
  | OpenIncreaseOfShareCapitalSubscribersAllocationEvent
  | OpenIncreaseOfShareCapitalSubscribersStatusEvent
  | OpenMobileDocumentPreviewEvent
  | OpenReviewDialogAction
  | OpenBundleSelectionDialogAction
  | OpenPackageSigningDialogAction
  | OpenSendForReviewDialogAction
  | OpenSigningStatusDialogAction
  | OpenChangePermissionDialogAction
  | OpenSaveAsTemplateDialog
  | OpenDocumentBlobEvent
  | OpenSubscriptionStatusEvent
  | OpenTabEvent
  | OpenViewingStatusDialogAction
  | ProjectValidationFailedEvent
  | RefreshProjectStatusEvent
  | RefreshProjectStatusResultEvent
  | RefreshSigningStatusesAction
  | RegisterNewSignerEvent
  | RegisterNewSignerResultEvent
  | ReloadProjectEvent
  | RemoveDecisionEvent
  | RemoveDocumentsEvent
  | RemoveCustomPanelEvent
  | ReopenDocumentEvent
  | ReopenDocumentResultEvent
  | ResendDocumentForSigningEvent
  | ResendDocumentForSigningResultEvent
  | ResendNoticeOfBoardMeetingEvent
  | ResendNoticeOfBoardMeetingResultEvent
  | ResendNoticeToUpdatedRecipientEvent
  | ResendNoticeToUpdatedRecipientResultEvent
  | SaveGeneralMeetingVotesEvent
  | SaveGeneralMeetingVotesResultEvent
  | SaveParticipationEvent
  | SaveProjectNameEvent
  | SaveProjectScheduledEvent
  | SaveProjectSuccessEvent
  | SaveSelectedBatchCompaniesEvent
  | SaveShareAllocationEvent
  | SaveShareAllocationResultEvent
  | SearchCompaniesEvent
  | SearchCompaniesResultEvent
  | SearchMatrikkelAddressEvent
  | SearchMatrikkelAddressResultEvent
  | VerifyMatrikkelAddressEvent
  | VerifyMatrikkelAddressResultEvent
  | CheckProviderValidityEvent
  | CheckProviderValidityResultEvent
  | CheckCompanyNameEvent
  | CheckCompanyNameResultEvent
  | SearchNaceEvent
  | SearchNaceResultEvent
  | SearchMunicipalitiesEvent
  | SearchMunicipalitiesResultEvent
  | SelectCompanyEvent
  | SendDocumentForSigningEvent
  | SendDocumentForSigningResultEvent
  | SendMultipleDocumentsForSigningEvent
  | SendMultipleDocumentsForSigningResultEvent
  | SendDocumentForViewingEvent
  | SendDocumentForViewingResultEvent
  | SendForReviewEvent
  | SendForReviewResultEvent
  | SendSubscriptionFormForSigningEvent
  | SendSubscriptionFormForSigningResultEvent
  | SendToBREvent
  | SendIncorporationToBREvent
  | CheckIncorporationStatusEvent
  | CheckIncorporationStatusResultEvent
  | SimulateBrResponseEvent
  | CreateMergerCounterpartEvent
  | CreateMergerCounterpartResultEvent
  | FetchIncorporationActivationInfoEvent
  | FetchIncorporationActivationInfoResultEvent
  | SelectIncorporationActivationSubscriptionEvent
  | PrekontrollIncorporationEvent
  | PrekontrollIncorporationResultEvent
  | SkipSendToBREvent
  | SkipSendToBRResultEvent
  | SendToBRResultEvent
  | ToggleDocumentPanelEvent
  | ToggleFullscreenEvent
  | ToggleMaximizeDetailsPanelEvent
  | ToggleMeetingScreenShareModeEvent
  | UnlockDocumentAction
  | UpdateBoardMeetingRecipientsEvent
  | UpdateBoardMeetingRecipientsResultEvent
  | UpdateBoardMeetingParticipantsEvent
  | UpdateCompanyRoleEmailsEvent
  | UpdateCompanyRoleEmailsResultEvent
  | UpdateDecisionTextsEvent
  | UpdateDecisionIntroTextsEvent
  | UpdateElectronicGeneralMeetingStatusEvent
  | UpdateElectronicGeneralMeetingStatusResultEvent
  | UpdateUploadingAttachmentPropsEvent
  | UpdateFollowUpStatusEvent
  | UpdateFollowUpStatusResultEvent
  | UpdateGeneralMeetingEvent
  | UpdateInputStateEvent
  | UpdateMinutesLiveParticipationEvent
  | UpdateShareholderEmailBatchEvent
  | UpdateShareholderEmailEvent
  | UpdateStateEvent
  | UpdateVotingInGeneralMeetingEvent
  | UpdateAllVotesInGeneralMeetingEvent
  | UpdateVotingTypeInGeneralMeetingEvent
  | UploadAttachmentsEvent
  | UploadDocumentBlobEvent
  | ValidateProjectEvent
  | ValidateProjectAction
  | UpdateDocumentPreview
  | ShowGeneralMeetingDialogEvent;

export const isDbotEvent = (event: object): event is DbotEvent =>
  "type" in event && String(event.type).startsWith("dbot/");

export function mapLocalesToDocumentLanguageWithDefault(
  orgNo: string,
  locales?: Language[],
): DocumentLanguage {
  if (locales?.length === 0) {
    return defaultLanguageForOrgNo(orgNo);
  } else {
    return mapLocalesToDocumentLanguage(locales ?? []);
  }
}

export function mapLocalesToDocumentLanguage(locales: Language[] = []): DocumentLanguage {
  if (locales.includes(Language.NO) && locales.includes(Language.EN)) {
    return "no/en";
  } else if (locales.includes(Language.SV) && locales.includes(Language.EN)) {
    return "sv/en";
  } else if (locales.includes(Language.EN)) {
    return "en";
  } else if (locales.includes(Language.SV)) {
    return "sv";
  }
  return "no";
}

export function mapDocumentLanguageToLocales(documentLanguage: DocumentLanguage) {
  switch (documentLanguage) {
    case "no":
      return [Language.NO];
    case "en":
      return [Language.EN];
    case "sv":
      return [Language.SV];
    case "no/en":
      return [Language.NO, Language.EN];
    case "sv/en":
      return [Language.SV, Language.EN];
  }
}

export const defaultDocumentLanguage = (orgNo: string): Language[] => [
  defaultLanguageForOrgNo(orgNo),
];

///
/// Initialization
///

export type CleanupFunction = () => void;

export const cleanupFunctions = (fns: CleanupFunction[]): CleanupFunction => {
  return () => {
    fns.forEach((f) => f());
  };
};

export interface WithInitialization {
  init: () => CleanupFunction;
}

///
/// Decision texts
///

export type DecisionTextModel = {
  model: LocalizedTextEditorModel;
  introTexts: LocalizedTextEditorModel;
  decisionId: string;
  title: TMessage;
  showDescription: BooleanFieldModel;
  showIntro: BooleanFieldModel;
};

export function mapDecisionTextModel(
  dispatch: (event: DbotEvent) => void,
  decision: DecisionItem,
  documentType: DocumentType,
  context: ProjectContext,
  documentLanguages: Language[],
): DecisionTextModel {
  const dispatchTextChange = (
    translationOption: TranslationOption,
    locale: Language,
    text?: string,
  ) => {
    dispatch(
      updateDecisionText(decision.decisionId, documentType, locale, text, translationOption),
    );
  };
  const dispatchIntroTextChange = (
    translationOption: TranslationOption,
    locale: Language,
    text?: string,
  ) => {
    dispatch(
      updateDecisionIntroText(decision.decisionId, documentType, locale, text, translationOption),
    );
  };

  const model = createDecisionTextsModel({
    label: {
      key: "decisions.decisionTextIn",
      params: { document: getDocumentLabel(documentType) },
    },
    texts: decision.decisionTexts,
    documentType,
    documentLanguages,
    context,
    onChange: dispatchTextChange,
  });
  const introTexts = createDecisionTextsModel({
    label: {
      key: "decisions.decisionIntroTextIn",
      params: { document: getDocumentLabel(documentType) },
    },
    texts: decision.decisionIntroTexts,
    documentType,
    documentLanguages,
    context,
    onChange: dispatchIntroTextChange,
  });

  return {
    model,
    introTexts,
    decisionId: decision.decisionId,
    title: model.label,
    showDescription: new BooleanFieldModel({ value: !model.isEmpty }),
    showIntro: new BooleanFieldModel({ value: !introTexts.isEmpty }),
  };
}

export function createDecisionTextsModel({
  label,
  texts,
  documentType,
  documentLanguages,
  context,
  onChange,
}: {
  label: TMessage;
  texts: LocalizedTextForDocument[];
  documentType: DocumentType;
  documentLanguages: Language[];
  context: ProjectContext;
  onChange: (translationOption: TranslationOption, locale: Language, text?: string) => void;
}) {
  return new LocalizedTextEditorModel({
    isReadOnly() {
      return context.isLocked;
    },
    label,
    texts: documentLanguages.map<LocalizedText>((language) => {
      const decisionText = texts.find(
        (t) => t.documentType === documentType && t.language === language,
      );
      return {
        text: decisionText?.text ?? "",
        language,
        translationOption: decisionText?.translationOption,
      };
    }),
    onChange,
    aiClient: context.aiClient,
  });
}

export function mapLocalizedDecisionTextsToModel(
  context: ProjectContext,
  dispatch: DbotEventDispatcher,
  documentType: DocumentType,
  documentLanguage: DocumentLanguage,
) {
  const documentLanguages = mapDocumentLanguageToLocales(documentLanguage);
  return context.currentDecisions.map<DecisionTextModel>((decision) => {
    return mapDecisionTextModel(dispatch, decision, documentType, context, documentLanguages);
  });
}

export function getDecisionName(
  type: DecisionType,
  data?: DecisionData,
  lang?: Language,
): TMessage {
  if (data?.type === DecisionType.freetext) {
    const title =
      data?.localizedTitles.find((x) => x.language === lang) ?? data?.localizedTitles[0];
    return title ? { translated: title.text } : { key: `decisionType.${data.type}.action` };
  }
  return { key: `decisionType.${type}.name` };
}

export function getDecisionActionLabel(type: DecisionType, data?: DecisionData): TMessage {
  if (type === DecisionType.freetext) {
    return getDecisionName(type, data);
  }
  return { key: `decisionType.${type}.action` };
}

const sendToBRDescriptionKeyByDecisionType: Record<DecisionType, TMessageKey | undefined> = {
  [DecisionType.accountant]: "decisionType.accountant.br-help-text",
  [DecisionType.acquireOwnSharesPowerOfAttorney]:
    "decisionType.acquireOwnSharesPowerOfAttorney.br-help-text",
  [DecisionType.approveAnnualAccount]: undefined,
  [DecisionType.approveAuditorRemuneration]: undefined,
  [DecisionType.approveCompanyBoardRemuneration]: undefined,
  [DecisionType.approveNominationCommitteeRemuneration]: undefined,
  [DecisionType.assignsNewCompanyBoard]: "decisionType.assignsNewCompanyBoard.br-help-text",
  [DecisionType.auditor]: "decisionType.auditor.br-help-text",
  [DecisionType.boardAssignsSignature]: "decisionType.boardAssignsSignature.br-help-text",
  [DecisionType.capitalIncreaseThroughBonusIssue]:
    "decisionType.capitalIncreaseThroughBonusIssue.br-help-text",
  [DecisionType.changeArticlesOfAssociation]:
    "decisionType.changeArticlesOfAssociation.br-help-text",
  [DecisionType.changeBusinessPurpose]: "decisionType.changeBusinessPurpose.br-help-text",
  [DecisionType.changeCompanyBoardInArticlesOfAssociation]:
    "decisionType.changeCompanyBoardInArticlesOfAssociation.br-help-text",
  [DecisionType.changeOfAddress]: "decisionType.changeOfAddress.br-help-text",
  [DecisionType.changeOfBusinessManager]: "decisionType.changeOfBusinessManager.br-help-text",
  [DecisionType.changeSignatureInArticlesOfAssociation]:
    "decisionType.changeSignatureInArticlesOfAssociation.br-help-text",
  [DecisionType.companyName]: "decisionType.companyName.br-help-text",
  [DecisionType.conversionOfShares]: "decisionType.conversionOfShares.br-help-text",
  [DecisionType.convertibleLoanPowerOfAttorney]:
    "decisionType.convertibleLoanPowerOfAttorney.br-help-text",
  [DecisionType.dividend]: undefined,
  [DecisionType.dissolveCompany]: "decisionType.dissolveCompany.br-help-text",
  [DecisionType.liquidateCompany]: "decisionType.liquidateCompany.br-help-text",
  [DecisionType.dividendPowerOfAttorney]: "decisionType.dividendPowerOfAttorney.br-help-text",
  [DecisionType.electNominationCommittee]: undefined,
  [DecisionType.electionOfCompanyBoard]: "decisionType.electionOfCompanyBoard.br-help-text",
  [DecisionType.employmentOfManagingDirector]:
    "decisionType.employmentOfManagingDirector.br-help-text",
  [DecisionType.freetext]: undefined,
  [DecisionType.groupContributionByGeneralMeeting]: undefined,
  [DecisionType.increaseOfShareCapital]: "decisionType.increaseOfShareCapital.br-help-text",
  [DecisionType.increaseShareCapitalPowerOfAttorney]:
    "decisionType.increaseShareCapitalPowerOfAttorney.br-help-text",
  [DecisionType.insertCompanyBoardInArticlesOfAssociation]:
    "decisionType.insertCompanyBoardInArticlesOfAssociation.br-help-text",
  [DecisionType.interimBalanceSheet]: "decisionType.interimBalanceSheet.br-help-text",
  [DecisionType.insertSignatureInArticlesOfAssociation]:
    "decisionType.insertSignatureInArticlesOfAssociation.br-help-text",
  [DecisionType.noAccountant]: "decisionType.noAccountant.br-help-text",
  [DecisionType.noAuditor]: "decisionType.noAuditor.br-help-text",
  [DecisionType.noBusinessManager]: "decisionType.noBusinessManager.br-help-text",
  [DecisionType.procura]: "decisionType.procura.br-help-text",
  [DecisionType.noManagingDirector]: "decisionType.noManagingDirector.br-help-text",
  [DecisionType.shareTransferConsent]: undefined,
  [DecisionType.optionGrantLetter]: undefined,
  [DecisionType.optionExercise]: undefined,
  [DecisionType.reductionOfShareCapital]: "decisionType.reductionOfShareCapital.br-help-text",
  [DecisionType.removeCompanyBoardFromArticlesOfAssociation]:
    "decisionType.removeCompanyBoardFromArticlesOfAssociation.br-help-text",
  [DecisionType.removeSignatureFromArticlesOfAssociation]:
    "decisionType.removeSignatureFromArticlesOfAssociation.br-help-text",
  [DecisionType.reverseSplitShare]: "decisionType.reverseSplitShare.br-help-text",
  [DecisionType.splitShare]: "decisionType.splitShare.br-help-text",
  [DecisionType.incorporation]: "decisionType.incorporation.br-help-text",
  [DecisionType.merger]: undefined,
};

const sendToBRRegistrationDescriptionKeyByDecisionType: Partial<Record<DecisionType, TMessageKey>> =
  {
    [DecisionType.reductionOfShareCapital]:
      "decisionType.reductionOfShareCapital.br-help-text.registration",
    [DecisionType.merger]: "decisionType.merger.br-help-text.registration",
  };

const sendToBRCompletionDescriptionKeysByDecisionType: Partial<
  Record<DecisionType, { pending: TMessageKey; active: TMessageKey }>
> = {
  [DecisionType.reductionOfShareCapital]: {
    pending: "decisionType.reductionOfShareCapital.br-help-text.completion",
    active: "decisionType.reductionOfShareCapital.br-help-text.completion.isActive",
  },
  [DecisionType.merger]: {
    pending: "decisionType.merger.br-help-text.completion",
    active: "decisionType.merger.br-help-text.completion.isActive",
  },
};

export function getDecisionSendToBRDescription(
  type: DecisionType,
  isRegistration: boolean,
  followUps?: FollowUp[],
): TMessage {
  const brRegistrationDate = enableSendToBRDate(type, followUps);
  const completionKeys = sendToBRCompletionDescriptionKeysByDecisionType[type];
  if (!isRegistration && brRegistrationDate && completionKeys) {
    return {
      key: isBefore(new Date(), brRegistrationDate)
        ? completionKeys.pending
        : completionKeys.active,
      params: {
        date: {
          type: "date",
          value: brRegistrationDate,
        },
      },
    };
  }
  const key: TMessageKey =
    (isRegistration && sendToBRRegistrationDescriptionKeyByDecisionType[type]) ||
    sendToBRDescriptionKeyByDecisionType[type] ||
    "dbot.summary.sendToBR.description";
  return {
    key,
  };
}

const brApprovedRegistrationKeyByDecisionType: Partial<Record<DecisionType, TMessageKey>> = {
  [DecisionType.reductionOfShareCapital]:
    "decisionType.reductionOfShareCapital.br-approved.registration",
  [DecisionType.merger]: "decisionType.merger.br-approved.registration",
};

export function getDecisionApprovedInBRDescription(
  type: DecisionType,
  isRegistration: boolean,
  locale: Language,
  brPublishingDate?: string,
  brRegistrationDate?: Date,
): TMessage {
  const registrationKey = brApprovedRegistrationKeyByDecisionType[type];
  return isRegistration && brRegistrationDate && registrationKey
    ? {
        key: registrationKey,
        params: {
          brPublishingDate: formatDate(locale, brPublishingDate),
          deadline: formatDate(locale, brRegistrationDate),
        },
      }
    : {
        key: "dbot.summary.isApprovedInBR",
        params: { date: formatDate(locale, brPublishingDate) },
      };
}

export function getDecisionAttachmentsDescription(
  attachmentType: AttachmentType,
): TMessage | undefined {
  switch (attachmentType) {
    case AttachmentType.CONFIRMATION_SHARE_CAPITAL_CONTRIBUTIONS:
      return {
        key: "dbot.requiredAttachments.CONFIRMATION_SHARE_CAPITAL_CONTRIBUTIONS.description",
      };
    case AttachmentType.CONFIRMATION_SHARE_CAPITAL_NON_CASH_CONTRIBUTIONS:
      return {
        key: "dbot.requiredAttachments.CONFIRMATION_SHARE_CAPITAL_NON_CASH_CONTRIBUTIONS.description",
      };
    case AttachmentType.CONFIRMATION_SHARE_CAPITAL_DEBT_CONTRIBUTION:
      return {
        key: "dbot.requiredAttachments.CONFIRMATION_SHARE_CAPITAL_DEBT_CONTRIBUTION.description",
      };
    case AttachmentType.CONFIRMATION_SHARE_CAPITAL_KIND_CONTRIBUTION:
      return {
        key: "dbot.requiredAttachments.CONFIRMATION_SHARE_CAPITAL_KIND_CONTRIBUTION.description",
      };
    case AttachmentType.CONFIRMATION_SHARE_CAPITAL_AUDITOR:
      return {
        key: "dbot.requiredAttachments.CONFIRMATION_SHARE_CAPITAL_AUDITOR.description",
      };
    case AttachmentType.CONFIRMATION_SHARE_CAPITAL_REDUCTION:
      return {
        key: "dbot.requiredAttachments.CONFIRMATION_SHARE_CAPITAL_REDUCTION.description",
      };
    default:
      return undefined;
  }
}

export function getDecisionAttachmentsEmailTitle(
  attachmentType: AttachmentType,
): TMessage | undefined {
  switch (attachmentType) {
    case AttachmentType.CONFIRMATION_SHARE_CAPITAL_CONTRIBUTIONS:
      return {
        key: "dbot.requiredAttachments.CONFIRMATION_SHARE_CAPITAL_CONTRIBUTIONS.email",
      };
    default:
      return undefined;
  }
}

export function getDecisionAttachmentsEmailInformation(
  attachmentType: AttachmentType,
): TMessage | undefined {
  switch (attachmentType) {
    case AttachmentType.CONFIRMATION_SHARE_CAPITAL_CONTRIBUTIONS:
      return {
        key: "dbot.requiredAttachments.CONFIRMATION_SHARE_CAPITAL_CONTRIBUTIONS.email.information",
      };
    default:
      return undefined;
  }
}

export function getDecisionIcon(data?: DecisionData | DecisionType): IconName {
  if (
    data === DecisionType.freetext ||
    (!isDecisionType(data) &&
      data?.type === DecisionType.freetext &&
      freetextType(data) === FreetextType.INFORMATION_MATTER)
  ) {
    return "chats";
  }
  return "gavel";
}

export function getMatterTypeLabel(data?: DecisionData): TMessage {
  if (
    data?.type === DecisionType.freetext &&
    freetextType(data) === FreetextType.INFORMATION_MATTER
  ) {
    return { key: "dbot.information-matter" };
  }
  return { key: "dbot.decision-matter" };
}

export function freetextType(data?: Freetext): FreetextType {
  if (data?.freetextTypeForAllDocuments === FreetextType.CUSTOMIZED) {
    return data.customizedFreetextTypePerDocument.some(
      (x) => x.customizedFreetextType === CustomizedFreetextType.DECISION_MATTER,
    )
      ? FreetextType.DECISION_MATTER
      : FreetextType.INFORMATION_MATTER;
  }
  return data?.freetextTypeForAllDocuments ?? FreetextType.INFORMATION_MATTER;
}

export function decisionToDetailsPanel(
  decision: DecisionItem,
  { documentId }: { documentId?: string } = {},
): DetailsPanel {
  return {
    type: "decision",
    id: decision.decisionId,
    documentId: documentId,
    icon: getDecisionIcon(decision.decisionData),
    heading: decision.title,
    label: getMatterTypeLabel(decision.decisionData),
    hasValidationError: decision.hasValidationError,
  };
}

export function getDecisionDetailsPanels({
  currentDecisions,
  documentId,
}: {
  documentId?: string;
  currentDecisions: DecisionItem[];
}) {
  return currentDecisions.map((d) => decisionToDetailsPanel(d, { documentId }));
}

export function getRemoveEventForPanel(
  panel: DetailsPanel | undefined,
  context: { isLocked: boolean },
): DbotEvent | DbotEvent[] | undefined {
  if (!panel || panel.isDisabled || context.isLocked) {
    return;
  }
  switch (panel.type) {
    case "decision":
      return {
        type: "dbot/remove-decision",
        decisionId: panel.id,
        onSuccess: { type: "dbot/close-details-panel" },
      };
    case "company":
      return {
        type: "dbot/change-state",
        state: removeBatchCompanyState(panel.id),
      };
    case "custom":
      if (panel.canRemove) {
        return [
          {
            type: "dbot/remove-custom-panel",
            id: panel.id,
          },
          { type: "dbot/close-details-panel" },
        ];
      }
  }
}

export function detailsPanelToRef(panel: DetailsPanel): DetailsPanelRef;
export function detailsPanelToRef(panel: DetailsPanel | undefined): DetailsPanelRef | undefined {
  switch (panel?.type) {
    case "decision":
      return {
        type: "decision",
        decisionId: panel.id,
        documentId: panel.documentId,
      };
    case "custom":
      return {
        type: "custom",
        id: panel.id,
        documentId: panel.documentId,
      };
    case "follow-up":
      return when(panel.followUp, (followUp) => ({
        type: "follow-up",
        followUpType: followUp.item.data.type,
        orgNo: followUp.orgNo,
        decisionId: followUp.decisionId,
        attachmentType: followUp.attachmentType,
        isBRRegistration: followUp.isBRRegistration,
      }));
    case "generalmeeting":
      return when(panel.generalMeetingStep, (step) => ({
        type: "generalmeeting",
        id: panel.id,
        step,
      }));
    case "company":
      return { type: "company", orgNo: panel.id };
  }
}

export function getOpenNextPanelEvent(
  id: string | undefined,
  panels: DetailsPanel[],
): DbotEvent | undefined {
  const nextIndex = panels.findIndex((p) => p.id === id) + 1;
  const nextPanel = detailsPanelToRef(panels[nextIndex]);
  if (nextPanel) {
    return {
      type: "dbot/open-details-panel",
      panel: nextPanel,
    };
  }
}

export function getOpenPrevPanelEvent(
  id: string | undefined,
  panels: DetailsPanel[],
): DbotEvent | undefined {
  const prevIndex = panels.findIndex((p) => p.id === id) - 1;
  const prevPanel = detailsPanelToRef(panels[prevIndex]);
  if (prevPanel) {
    return {
      type: "dbot/open-details-panel",
      panel: prevPanel,
    };
  }
}

export function getGeneralMeetingDialogEvent(panel: GeneralMeetingDetailsPanel): DbotEvent {
  switch (panel.step.type) {
    case "registration":
      return {
        type: "dbot/show-general-meeting-dialog",
      };
    case "choiceOfMeetingChair":
      return {
        type: "dbot/show-general-meeting-dialog",
        voteType: GeneralMeetingVoteType.meetChair,
      };
    case "approveAgenda":
      return {
        type: "dbot/show-general-meeting-dialog",
        voteType: GeneralMeetingVoteType.approveNoticeAndAgenda,
      };
    case "decision":
      return {
        type: "dbot/show-general-meeting-dialog",
        voteType: GeneralMeetingVoteType.decision,
        decisionId: panel.step.decisionId,
      };
  }
}

export function getOpenNextGeneralMeetingDialogEvents(
  id: string | undefined,
  panels: DetailsPanel[],
): DbotEvent[] | undefined {
  const nextIndex = panels.findIndex((p) => p.id === id) + 1;
  const nextPanel = detailsPanelToRef(panels[nextIndex]);
  if (nextPanel && nextPanel.type === "generalmeeting") {
    return [
      { type: "dbot/open-details-panel", panel: nextPanel },
      getGeneralMeetingDialogEvent(nextPanel),
    ];
  }
}

export function getOpenPrevGeneralMeetingDialogEvents(
  id: string | undefined,
  panels: DetailsPanel[],
): DbotEvent[] | undefined {
  const nextIndex = panels.findIndex((p) => p.id === id) - 1;
  const nextPanel = detailsPanelToRef(panels[nextIndex]);
  if (nextPanel && nextPanel.type === "generalmeeting") {
    return [
      { type: "dbot/open-details-panel", panel: nextPanel },
      getGeneralMeetingDialogEvent(nextPanel),
    ];
  }
}

export const isDocumentLocked = (
  { documentStatuses }: { documentStatuses: ProjectDocumentStatus[] },
  documentId: string,
): boolean => documentStatuses.some((d) => d.documentId === documentId && d.isLocked);

export const anyDocumentSentForSigning = (documentStatuses: ProjectDocumentStatus[]): boolean =>
  documentStatuses.some(
    (d) =>
      d.signingStatus === DocumentSigningStatus.SENT_FOR_SIGNING ||
      d.signingStatus === DocumentSigningStatus.SIGNING_IN_PROGRESS,
  );

export const isDocumentSentForSigning = (
  { documentStatuses }: { documentStatuses: ProjectDocumentStatus[] },
  documentId: string,
): boolean =>
  documentStatuses.some(
    (d) =>
      d.documentId === documentId &&
      (d.signingStatus === DocumentSigningStatus.SENT_FOR_SIGNING ||
        d.signingStatus === DocumentSigningStatus.SIGNING_IN_PROGRESS),
  );

export const isDocumentSentForReview = (
  { documentStatuses }: { documentStatuses: ProjectDocumentStatus[] },
  documentId: string,
): boolean =>
  documentStatuses.some(
    (d) => d.documentId === documentId && d.signingStatus === DocumentSigningStatus.SENT_FOR_REVIEW,
  );

export const isDocumentSentForVoting = (
  { documentStatuses }: { documentStatuses: ProjectDocumentStatus[] },
  documentId: string,
): boolean =>
  documentStatuses.some(
    (d) => d.documentId === documentId && d.signingStatus === DocumentSigningStatus.SENT_FOR_VOTING,
  );

export const isDocumentSigned = (
  { documentStatuses }: { documentStatuses: ProjectDocumentStatus[] },
  documentId: string,
): boolean =>
  documentStatuses.some(
    (d) =>
      d.documentId === documentId && d.signingStatus === DocumentSigningStatus.SIGNING_COMPLETE,
  );

export const projectTemplateItems = (
  project: GetProjectResponse,
  language: Language,
): { decisions: TemplateItem[]; documents: TemplateItem[] } => ({
  decisions: project.jsonData.decisions.map((decision) => ({
    id: decision.decisionId,
    label: getDecisionName(decision.decisionData.type, decision.decisionData, language),
    attachments: templateAttachments(decision.attachments),
    isSealed: false,
  })),
  documents: project.jsonData.documents.map((document) => ({
    id: document.documentId,
    label:
      templateDocumentTitle(
        document.documentData.type === DocumentType.custom_document
          ? document.documentData.title
          : undefined,
        language,
      ) ?? getDocumentLabel(document.documentData.type),
    attachments: templateAttachments(document.attachments),
    isSealed: isDocumentSigned(project, document.documentId),
  })),
});

const templateAttachments = (
  attachments?: { id: string; filename: string }[],
): TemplateAttachment[] => (attachments ?? []).map(({ id, filename }) => ({ id, filename }));

export const isDocumentUploadedToSharepoint = (
  { documentStatuses }: { documentStatuses: ProjectDocumentStatus[] },
  documentId: string,
): boolean =>
  documentStatuses.some(
    (d) =>
      d.documentId === documentId &&
      d.eventLogs.some((e) => e.eventType === EventType.SharepointSyncEvent),
  );

export const isDocumentSent = (
  { documentStatuses }: { documentStatuses: ProjectDocumentStatus[] },
  documentId: string,
) => documentStatuses.some((d) => d.documentId === documentId && d.sentAt);

export const getDocumentStatus = (
  { documentStatuses }: { documentStatuses: ProjectDocumentStatus[] },
  documentId: string,
) => documentStatuses.find((d) => d.documentId === documentId);

export const documentReopenLabel = (
  context: { documentStatuses: ProjectDocumentStatus[] },
  documentId: string,
): TMessage =>
  isDocumentSigned(context, documentId)
    ? { key: "dbot.action.reopen" }
    : { key: "dbot.action.continue-editing" };

const documentStatusesAllowingEditing = [
  DocumentSigningStatus.SENT_FOR_SIGNING,
  DocumentSigningStatus.SIGNING_IN_PROGRESS,
  DocumentSigningStatus.SIGNING_COMPLETE,
  DocumentSigningStatus.UNKNOWN,
];

export const canDocumentBeReopened = (
  context: { documentStatuses: ProjectDocumentStatus[] },
  documentId: string,
): boolean =>
  context.documentStatuses.some(
    (d) =>
      d.documentId === documentId &&
      d.sentAt &&
      documentStatusesAllowingEditing.includes(d.signingStatus),
  );

export function isLanguageChanged(
  documentLanguages: Language[],
  textModels: LocalizedStringFieldModel[],
): boolean {
  return !isEqual(sortBy(documentLanguages), sortBy(textModels.map((t) => t.locale)));
}

export const toCompanyDetailsPanel = ({
  name,
  orgNo,
  isMainCompany,
}: {
  name: string;
  orgNo: string;
  isMainCompany?: boolean;
}): DetailsPanel => ({
  type: "company",
  id: orgNo,
  label: { translated: formatOrgNum(orgNo) },
  heading: { translated: name },
  icon: "buildings",
  isDisabled: isMainCompany,
});

export const getCompanyDetailsPanels = (ctx: ProjectContext): DetailsPanel[] => {
  const mergerWithCounterpart = findMergerWithCounterpart(ctx.currentDecisions);
  return mergerWithCounterpart
    ? getMergerCompanyPanels(mergerWithCounterpart)
    : ctx.companies.map(toCompanyDetailsPanel);
};

export function isInformationMatterInDocument(
  decisionData: DecisionData | undefined,
  documentType: DocumentType,
) {
  return (
    (decisionData?.type === DecisionType.freetext &&
      decisionData.freetextTypeForAllDocuments === FreetextType.INFORMATION_MATTER) ||
    (decisionData?.type === DecisionType.freetext &&
      decisionData.freetextTypeForAllDocuments === FreetextType.CUSTOMIZED &&
      decisionData.customizedFreetextTypePerDocument.some((d) => {
        return (
          d.documentType === documentType &&
          d.customizedFreetextType === CustomizedFreetextType.INFORMATION_MATTER
        );
      }))
  );
}

export const getDecisionAttachmentsInDocuments = (
  decisionItem: DecisionItem,
  documentTypes: DocumentType[],
): { id: string; filename: string }[] =>
  decisionItem.attachments.filter((a) =>
    documentTypes.some((dt) => a.selectedDocuments.includes(dt)),
  );

export const supportsAttachments = (context: ProjectContext) => !context.isBatchProject;

export const updateArticlesModeModel = (
  value: boolean | undefined,
): RadioFieldModel<boolean | undefined> => {
  return new RadioFieldModel<boolean | undefined>({
    value,
    required: true,
    options: [true, false],
    idFn: (value) => (value === undefined ? "undefined" : String(value)),
    labelFn: (value) =>
      value === true
        ? {
            key: "decisions.increaseShareCapitalAgainstCash.updateArticlesAutomatically.automatic",
          }
        : value === false
          ? {
              key: "decisions.increaseShareCapitalAgainstCash.updateArticlesAutomatically.manual",
            }
          : { translated: "" },
  });
};

export function findStateForDecision(inputStates: InputState[], decisionId: string) {
  return inputStates.find((i) => i.decisionId === decisionId) ?? last(inputStates);
}

export function makeCountryRadioFieldModel(locale: Language, selectedCountry?: string) {
  const countries = getAllCountriesSortedByName(locale);
  const countryCode = selectedCountry ?? norway;
  return new RadioFieldModel<{ code: string; name: string }>({
    options: countries,
    value: countries.find((c) => c.code === countryCode) ?? countries[0],
    idFn: (item) => item.code,
    labelFn: (item) => ({ translated: item.name }),
  });
}

export function selectedDestinationFolderModel(
  entries: DestinationFolder[] = [],
  preselected?: DestinationFolder,
): RadioFieldModel<DestinationFolder | undefined> {
  return new RadioFieldModel<DestinationFolder | undefined>({
    value: preselected ?? entries[0],
    options: entries,
    idFn: (option) => option?.id ?? "",
    labelFn: (option) => (option ? getEntryLabel(option) : { key: "select" }),
    iconFn: (option) => (option?.rootFolder ? "folder" : "archive"),
  });
}

export function documentPreviewUrl({
  documentId,
  orgNo,
  recipientId,
  includeAttachments,
}: {
  documentId: string;
  orgNo: string;
  recipientId?: string | number;
  includeAttachments?: boolean;
}) {
  return `/api/document/view/pdf/${documentId}/${orgNo}${toQueryString({ recipientId, includeAttachments })}`;
}

export class SendDocumentForViewingModel {
  recipientsByCompany: {
    [orgNo: string]: RecipientsEditModel;
  };
  language: Language;
  aiClient: AIClient;
  dispatch: DbotEventDispatcher;
  documentId: string;
  isDocumentIndividualised: boolean;
  documents: ProjectDocumentPreview[];
  companies: CompanyItem[];
  recipientUpdateResult?: Result<TMessage, TMessage>;
  isSubmitting?: boolean = false;
  selectedCompany?: CompanyItem;
  selectedDocument?: ProjectDocumentPreview;
  onClose: () => void;
  title?: TMessage;

  constructor({
    recipientsByCompany,
    language,
    aiClient,
    dispatch,
    documentId,
    isDocumentIndividualised,
    documents,
    companies,
    recipientUpdateResult,
    onClose,
    title,
  }: {
    recipientsByCompany: {
      // noinspection JSUnusedLocalSymbols
      [orgNo: string]: RecipientsEditModel;
    };
    language: Language;
    aiClient: AIClient;
    dispatch: DbotEventDispatcher;
    documentId: string;
    isDocumentIndividualised: boolean;
    documents: ProjectDocumentPreview[];
    companies: CompanyItem[];
    recipientUpdateResult?: Result<TMessage, TMessage>;
    onClose: () => void;
    title?: TMessage;
  }) {
    makeAutoObservable(this, {}, { autoBind: true });
    this.recipientsByCompany = recipientsByCompany;
    this.language = language;
    this.aiClient = aiClient;
    this.dispatch = dispatch;
    this.documentId = documentId;
    this.isDocumentIndividualised = isDocumentIndividualised;
    this.documents = documents;
    this.companies = companies;
    this.recipientUpdateResult = recipientUpdateResult;
    this.onClose = onClose;
    this.title = title;
    this.setSelectedCompany(companies[0]);
  }

  get recipientsAlert(): AlertProps | undefined {
    switch (this.recipientUpdateResult?.state) {
      case "ok":
        return {
          type: "success",
          message: this.recipientUpdateResult.data,
          onClose: this.dismissRecipientsAlert,
        };
      case "failure":
        return {
          type: "error",
          message: this.recipientUpdateResult.error,
          onClose: this.dismissRecipientsAlert,
        };
    }
  }

  get filePreview(): FilePreviewProps | undefined {
    if (this.selectedDocument) {
      const reference = resolveUrnReference(this.selectedDocument?.reference);
      return {
        filename: `${this.selectedDocument.documentTitle}.pdf`,
        file: {
          url: documentPreviewUrl({
            documentId: this.selectedDocument.documentId,
            orgNo: this.selectedDocument.orgNo,
            recipientId: recipientIdFromUrnReference(reference),
          }),
        },
      };
    }
  }

  get documentsForPreview(): DocumentsForPreview {
    const documents = this.documents
      .filter((doc) => doc.orgNo === this.selectedCompany?.organisationNumber)
      .map((d) => ({
        id: d.documentId,
        name: d.documentTitle,
        reference: d.reference,
      }));
    return {
      documents,
      selectedDocument: this.selectedDocument
        ? {
            id: this.selectedDocument.documentId,
            name: this.selectedDocument.documentTitle,
            reference: this.selectedDocument.reference,
          }
        : undefined,
      selectDocument: (d) => this.setSelectedDocument(d),
      previousDocument: when(
        prev(
          documents,
          (s) =>
            s.id === this.selectedDocument?.documentId &&
            s.reference === this.selectedDocument?.reference,
        ),
        (s) => () => this.setSelectedDocument(s),
      ),
      nextDocument: when(
        next(
          documents,
          (s) =>
            s.id === this.selectedDocument?.documentId &&
            s.reference === this.selectedDocument?.reference,
        ),
        (s) => () => this.setSelectedDocument(s),
      ),
    };
  }

  get companiesForPreview(): CompaniesForPreview | undefined {
    return {
      companies: this.companies,
      selectedCompany: this.selectedCompany,
      selectCompany: (company) => this.setSelectedCompany(company),
      previousCompany: when(
        prev(
          this.companies,
          (c) => c.organisationNumber === this.selectedCompany?.organisationNumber,
        ),
        (s) => () => this.setSelectedCompany(s),
      ),
      nextCompany: when(
        next(
          this.companies,
          (c) => c.organisationNumber === this.selectedCompany?.organisationNumber,
        ),
        (s) => () => this.setSelectedCompany(s),
      ),
    };
  }

  get emailBody(): LocalizedTextEditorModel {
    return new LocalizedTextEditorModel({
      aiClient: this.aiClient,
      isReadOnly: () => false,
      label: { key: "customize-message" },
      texts: [{ language: this.language, text: "" }],
    });
  }

  dismissRecipientsAlert() {
    this.recipientUpdateResult = undefined;
  }

  setSelectedDocument(d: { id: string; reference?: string } | undefined) {
    this.selectedDocument = this.documents.find(
      (x) => x.documentId === d?.id && x.reference === d?.reference,
    );
  }

  setSelectedCompany(selectedCompany: CompanyItem) {
    this.selectedCompany = selectedCompany;
    this.setSelectedDocument(this.documentsForPreview.documents[0]);
  }

  onSubmit() {
    const models = Object.entries(this.recipientsByCompany).filter(([, m]) => !m.isEmpty);
    models.forEach(([, m]) => m.validate());
    if (models.length && models.every(([, m]) => m.isValid)) {
      this.isSubmitting = true;
      this.dispatch({
        type: "dbot/send-document-for-viewing",
        documentId: this.documentId,
        emailBody: this.emailBody.localizedTexts[0].text,
        recipients: models.flatMap(([orgNo, m]) => m.toRecipients(orgNo)),
        onSuccess: this.onClose,
        onFailure: this.onClose,
      });
    }
  }
}

export function excelFileUploadConfig<T extends ProjectContext>(
  contextOrForm: T | { context: T },
): FileUploadConfig {
  const context = "context" in contextOrForm ? contextOrForm.context : contextOrForm;
  return filterFileUploadConfig(context.fileUploadConfig, "xlsx");
}
